<template>
  <div class="player-page">
    <!-- Outdated Browser Modal -->
    <div
      v-if="showOutdatedBrowserModal"
      class="compat-modal-backdrop"
      role="dialog"
      aria-modal="true"
      aria-labelledby="compat-modal-title"
    >
      <div class="compat-modal">
        <h2 id="compat-modal-title">Your browser is out of date</h2>
        <p>
          Some features on this site may not work properly. Update your browser
          for the best experience.
        </p>
        <div class="compat-actions">
          <button
            class="btn-primary"
            @click="dismissOutdatedBrowserModal(false)"
          >
            OK
          </button>
          <button
            class="btn-secondary"
            @click="dismissOutdatedBrowserModal(true)"
          >
            Ignore for 1 hour
          </button>
        </div>
      </div>
    </div>

    <div class="movie-maker-wrapper" :class="{ hidden: !showMovieModal }">
      <div id="movie-maker-modal" ref="clipMakerModal">
        <p class="modal-title" v-if="clipModalMode !== 'downloaded'">
          Clip Maker
        </p>
        <div class="modal-body" v-if="clipModalMode == 'creating'">
          <div class="modal-wrapper start-wrapper">
            <p class="modal-subtitle">Start Time</p>
            <div class="modal-group start-group">
              <button
                @click="decrementStartTime"
                :disabled="disableClipElements"
              >
                -
              </button>
              <input
                type="datetime-local"
                v-model="startTime"
                :disabled="disableClipElements"
              />
              <button
                @click="incrementStartTime"
                :disabled="disableClipElements"
              >
                +
              </button>
            </div>
          </div>
          <div class="modal-wrapper end-wrapper">
            <p class="modal-subtitle">End Time</p>
            <div class="modal-group end-group">
              <button @click="decrementEndTime" :disabled="disableClipElements">
                -
              </button>
              <input
                type="datetime-local"
                v-model="endTime"
                :disabled="disableClipElements"
              />
              <button @click="incrementEndTime" :disabled="disableClipElements">
                +
              </button>
            </div>
          </div>
        </div>

        <div class="modal-body modal-body-checkmark" v-else>
          <div class="checkmark-box">
            <div class="checkmark"></div>
            <h2>Download Complete</h2>
          </div>
        </div>

        <div class="modal-footer" v-if="clipModalMode == 'creating'">
          <button
            class="modal-close"
            @click="closeClipMaker"
            :disabled="downloadingClip"
          >
            Cancel
          </button>

          <div class="export-group" ref="exportGroup">
            <!-- Main action button runs the selected action -->
            <button
              class="modal-accept export-main"
              :disabled="exportMainDisabled"
              @click="performExport"
              ref="exportBtn"
            >
              <template v-if="!downloadingClip">
                <span v-if="exportChoice === 'download'">Download Clip</span>
                <span v-else-if="exportChoice === 'google'">
                  Share to Google Drive
                </span>

                <span v-else>Share to OneDrive</span>
              </template>
              <template v-else>
                <LoadingWheel class="download-indicator" />
              </template>
            </button>

            <!-- Caret button toggles the dropdown -->
            <button
              class="modal-accept export-caret"
              :disabled="downloadingClip"
              aria-haspopup="menu"
              :aria-expanded="exportMenuOpen ? 'true' : 'false'"
              @click.stop="toggleExportMenu"
              title="Choose export destination"
            >
              ▾
            </button>

            <!-- Dropdown menu -->
            <ul
              v-show="exportMenuOpen"
              class="export-menu"
              role="menu"
              ref="exportMenu"
              @click.stop
            >
              <li role="menuitem">
                <button
                  class="menu-item"
                  :disabled="downloadDisabled"
                  @click="selectExport('download')"
                >
                  Download Clip
                </button>
              </li>
              <li role="menuitem">
                <button
                  class="menu-item"
                  :disabled="googleDisabled"
                  @click="selectExport('google')"
                  title="Sign in with Google first via the user menu"
                >
                  Share to Google Drive
                </button>
              </li>
              <li role="menuitem">
                <button
                  class="menu-item"
                  :disabled="microsoftDisabled"
                  @click="selectExport('microsoft')"
                  title="Sign in with Microsoft first via the user menu"
                >
                  Share to OneDrive
                </button>
              </li>
            </ul>
          </div>
        </div>
      </div>

      <div class="movie-maker-modal-backdrop" @click="closeClipMaker"></div>
    </div>

    <div class="sidebar">
      <nav>
        <TempestLogo />
      </nav>
      <div class="events">
        <!-- Events header with toggles -->
        <div class="section-header">
          <p class="title">Event Inbox</p>

          <button
            class="event-filter-btn"
            @click="refreshEvents"
            title="Refresh events"
          >
            <svg
              class="event-icon"
              viewBox="0 0 20 20"
              fill="currentColor"
              xmlns="http://www.w3.org/2000/svg"
            >
              <path
                d="M10 2C8.02093 2 6.20188 2.72229 4.80469 3.91667C4.73465 3.97242 4.67657 4.0417 4.63388 4.12039C4.5912 4.19907 4.5648 4.28554 4.55626 4.37465C4.54771 4.46376 4.5572 4.55367 4.58415 4.63903C4.6111 4.72439 4.65497 4.80345 4.71313 4.8715C4.77129 4.93954 4.84256 4.99518 4.92268 5.03509C5.00281 5.075 5.09015 5.09837 5.1795 5.1038C5.26885 5.10923 5.35838 5.09661 5.44275 5.0667C5.52712 5.03678 5.6046 4.99019 5.67057 4.92969C6.83605 3.9334 8.3444 3.33333 10 3.33333C13.4644 3.33333 16.3007 5.95862 16.6341 9.33333H14.6667L17.3333 13.3333L20 9.33333H17.9661C17.6253 5.23444 14.1852 2 10 2ZM2.66667 6.66667L0 10.6667H2.03385C2.37472 14.7656 5.81477 18 10 18C11.9791 18 13.7981 17.2777 15.1953 16.0833C15.2653 16.0276 15.3234 15.9583 15.3661 15.8796C15.4088 15.8009 15.4352 15.7145 15.4437 15.6253C15.4523 15.5362 15.4428 15.4463 15.4159 15.361C15.3889 15.2756 15.345 15.1965 15.2869 15.1285C15.2287 15.0605 15.1574 15.0048 15.0773 14.9649C14.9972 14.925 14.9099 14.9016 14.8205 14.8962C14.7312 14.8908 14.6416 14.9034 14.5573 14.9333C14.4729 14.9632 14.3954 15.0098 14.3294 15.0703C13.164 16.0666 11.6556 16.6667 10 16.6667C6.53563 16.6667 3.69934 14.0414 3.36589 10.6667H5.33333L2.66667 6.66667Z"
              />
            </svg>
          </button>

          <div
            class="event-filters"
            role="group"
            aria-label="Event type filters"
            v-if="eventsReversed.length != 0 && camera"
          >
            <!-- Person toggle -->
            <button
              class="event-filter-btn"
              :class="{ active: personOn }"
              :aria-pressed="personOn ? 'true' : 'false'"
              @click="togglePerson"
              title="Toggle: Person events"
            >
              <svg class="event-icon" viewBox="0 0 24 24" fill="currentColor">
                <use :href="`${eventTypeIcon('human')}#symbol`"></use>
              </svg>
            </button>

            <!-- Car toggle -->
            <button
              class="event-filter-btn"
              :class="{ active: carOn }"
              :aria-pressed="carOn ? 'true' : 'false'"
              @click="toggleCar"
              title="Toggle: Car events"
            >
              <svg class="event-icon" viewBox="0 0 24 24" fill="currentColor">
                <use :href="`${eventTypeIcon('car')}#symbol`"></use>
              </svg>
            </button>
          </div>
        </div>
        <p style="color: var(--text)" v-if="camera !== null">
          {{ camera.name }}
        </p>
        <p style="color: var(--text)" v-else>-</p>
        <div class="event-list" :key="eventsReversedKey" ref="eventList">
          <div class="eventLoading" v-if="newEventsIncoming">
            <LoadingWheel style="color: white"></LoadingWheel>
          </div>
          <div
            class="event center"
            v-else-if="eventsReversed.length == 0 || !camera"
          >
            No events to display
          </div>
          <template
            v-else
            v-for="(event, index) in eventsReversed"
            :key="'event-' + index"
          >
            <div
              class="event-group"
              v-if="eventFiltered(event)"
              :id="`event-${event.data.s}`"
            >
              <div
                class="event"
                :class="{
                  'is-now': eventDistanceToNow(event) < 500,
                  'is-soon': eventDistanceToNow(event) < 5000,
                }"
                @click="startEventPlayback(event)"
              >
                <div class="icon-group">
                  <svg
                    class="event-icon"
                    viewBox="0 0 24 24"
                    fill="currentColor"
                  >
                    <use :href="`${eventIcon(event)}#symbol`"></use>
                  </svg>
                  <svg
                    v-if="subEventIcon(event)"
                    class="event-icon"
                    viewBox="0 0 24 24"
                    fill="currentColor"
                  >
                    <use :href="`${subEventIcon(event)}#symbol`"></use>
                  </svg>
                  <p>
                    {{ eventLabel(event) }}
                    <span v-if="subEventType(event)">
                      + {{ subEventLabel(event) }}
                    </span>
                  </p>
                </div>
                <div class="info">
                  <p>{{ getEventDuration(event) }}</p>
                  <div class="spacer"></div>
                  <p>{{ getEventStartTime(event) }}</p>
                </div>
              </div>
              <button class="clip-btn" @click="openClipMaker(event)"></button>
            </div>
          </template>
        </div>
        <div class="date-buttons" v-if="showDateButtons">
          <button @click="offsetInc">&lt;</button>
          <span class="date-text" style="color: var(--text)">{{
            displayDate()
          }}</span>
          <div
            class="right-wrapper"
            :class="{ 'disable-right-buttons': this.dayOffset == 0 }"
          >
            <button
              class="offsetDec"
              @click="offsetDec"
              :disabled="this.dayOffset == 0"
            >
              &gt;
            </button>
            <button @click="offsetReset" :disabled="this.dayOffset == 0">
              now
            </button>
          </div>
        </div>
      </div>
    </div>
    <div class="right-pane" ref="rightPane">
      <div
        class="livestream"
        ref="livestreamFsTarget"
        :style="{ height: livePx + 'px' }"
      >
        <div class="homeview-video-wrapper">
          <LiveVideo
            ref="liveVideo"
            :key="camera?.id"
            :camera="camera"
            @cameraLoading="setCameraLoading"
            @seekTime="updateEvents"
            @startEventPlayback="startEventPlayback"
            @playmode="lvPlaymode"
            @clipStatus="handleClipStatus"
            @sessionId="setSessionId"
            @openClipMaker="openClipMakerCurrentTime"
            v-if="camera && camera.id != null"
            :holywoodMode="holywoodMode"
            :controlIFrameMode="controlIFrameMode"
            @prevCam="prevCam"
            @nextCam="nextCam"
            :eventTypes="internalEventTypes"
            @focusEvent="scrollToEvent"
            @preview-updated="handlePreviewUpdated"
            @toggle-fullscreen="toggleFullscreen"
            @time="onVideoTime"
            @dateChange="updateLastVideoDateEmitted"
          />
          <WebConsole
            :camId="consoleCamId"
            ref="webconsole"
            v-else-if="consoleCamId"
          />
          <LibPeer
            :camId="libpeerCamId"
            ref="libpeer"
            v-else-if="libpeerCamId"
          />
          <div class="no-cam-wrapper" v-else>
            <img
              class="no-cam-icon"
              src="@/assets/camera-off-24-filled.svg"
              height="75"
            />
            <h1 class="no-cam-text">
              Please select a camera below to view the stream.
            </h1>
          </div>
          <div
            class="overlay-buttons"
            ref="utilsMenu"
            @mouseenter="openUtilsMenu"
            @mouseleave="closeUtilsMenu"
          >
            <!-- Hamburger menu button -->
            <button ref="menuButton" class="overlay-icon-btn">
              <svg
                v-if="menuOpen"
                xmlns="http://www.w3.org/2000/svg"
                width="16"
                height="16"
                fill="currentColor"
                class="bi bi-menu-button-wide-fill"
                viewBox="0 0 16 16"
              >
                <path
                  d="M1.5 0A1.5 1.5 0 0 0 0 1.5v2A1.5 1.5 0 0 0 1.5 5h13A1.5 1.5 0 0 0 16 3.5v-2A1.5 1.5 0 0 0 14.5 0zm1 2h3a.5.5 0 0 1 0 1h-3a.5.5 0 0 1 0-1m9.927.427A.25.25 0 0 1 12.604 2h.792a.25.25 0 0 1 .177.427l-.396.396a.25.25 0 0 1-.354 0zM0 8a2 2 0 0 1 2-2h12a2 2 0 0 1 2 2v5a2 2 0 0 1-2 2H2a2 2 0 0 1-2-2zm1 3v2a1 1 0 0 0 1 1h12a1 1 0 0 0 1-1v-2zm14-1V8a1 1 0 0 0-1-1H2a1 1 0 0 0-1 1v2zM2 8.5a.5.5 0 0 1 .5-.5h9a.5.5 0 0 1 0 1h-9a.5.5 0 0 1-.5-.5m0 4a.5.5 0 0 1 .5-.5h6a.5.5 0 0 1 0 1h-6a.5.5 0 0 1-.5-.5"
                />
              </svg>
              <svg
                v-else
                xmlns="http://www.w3.org/2000/svg"
                width="16"
                height="16"
                fill="currentColor"
                class="bi bi-menu-button-wide"
                viewBox="0 0 16 16"
              >
                <path
                  d="M0 1.5A1.5 1.5 0 0 1 1.5 0h13A1.5 1.5 0 0 1 16 1.5v2A1.5 1.5 0 0 1 14.5 5h-13A1.5 1.5 0 0 1 0 3.5zM1.5 1a.5.5 0 0 0-.5.5v2a.5.5 0 0 0 .5.5h13a.5.5 0 0 0 .5-.5v-2a.5.5 0 0 0-.5-.5z"
                />
                <path
                  d="M2 2.5a.5.5 0 0 1 .5-.5h3a.5.5 0 0 1 0 1h-3a.5.5 0 0 1-.5-.5m10.823.323-.396-.396A.25.25 0 0 1 12.604 2h.792a.25.25 0 0 1 .177.427l-.396.396a.25.25 0 0 1-.354 0M0 8a2 2 0 0 1 2-2h12a2 2 0 0 1 2 2v5a2 2 0 0 1-2 2H2a2 2 0 0 1-2-2zm1 3v2a1 1 0 0 0 1 1h12a1 1 0 0 0 1-1v-2zm14-1V8a1 1 0 0 0-1-1H2a1 1 0 0 0-1 1v2zM2 8.5a.5.5 0 0 1 .5-.5h9a.5.5 0 0 1 0 1h-9a.5.5 0 0 1-.5-.5m0 4a.5.5 0 0 1 .5-.5h6a.5.5 0 0 1 0 1h-6a.5.5 0 0 1-.5-.5"
                />
              </svg>
            </button>
            <Transition name="menu-drop">
              <div
                class="menu-panel"
                :class="{ 'menu-open': menuOpen }"
                v-show="menuOpen || showUserInfo || showHelp"
              >
                <!-- Help menu -->
                <button
                  ref="helpButton"
                  class="overlay-icon-btn"
                  :class="{ open: showHelp }"
                  @click.stop="
                    showHelp = !showHelp;
                    showUserInfo = false;
                  "
                  :aria-expanded="showHelp ? 'true' : 'false'"
                  aria-haspopup="true"
                  title="Keyboard shortcuts"
                >
                  <p>Shortcuts</p>
                  <svg
                    xmlns="http://www.w3.org/2000/svg"
                    width="16"
                    height="16"
                    fill="currentColor"
                    class="bi bi-question"
                    viewBox="0 0 16 16"
                  >
                    <path
                      d="M5.255 5.786a.237.237 0 0 0 .241.247h.825c.138 0 .248-.113.266-.25.09-.656.54-1.134 1.342-1.134.686 0 1.314.343 1.314 1.168 0 .635-.374.927-.965 1.371-.673.489-1.206 1.06-1.168 1.987l.003.217a.25.25 0 0 0 .25.246h.811a.25.25 0 0 0 .25-.25v-.105c0-.718.273-.927 1.01-1.486.609-.463 1.244-.977 1.244-2.056 0-1.511-1.276-2.241-2.673-2.241-1.267 0-2.655.59-2.75 2.286m1.557 5.763c0 .533.425.927 1.01.927.609 0 1.028-.394 1.028-.927 0-.552-.42-.94-1.029-.94-.584 0-1.009.388-1.009.94"
                    />
                  </svg>
                </button>
                <!-- User info -->
                <button
                  class="overlay-icon-btn"
                  @click.stop="
                    showUserInfo = !showUserInfo;
                    showHelp = false;
                    exportMenuOpen = false;
                  "
                  :class="{ open: showUserInfo }"
                  :aria-expanded="showUserInfo ? 'true' : 'false'"
                  aria-haspopup="true"
                  title="User info"
                >
                  <p>Profile</p>
                  <svg
                    viewBox="0 0 24 24"
                    width="20"
                    height="20"
                    aria-hidden="true"
                  >
                    <path
                      fill="currentColor"
                      d="M12 12a5 5 0 1 0-5-5a5 5 0 0 0 5 5m0 2c-4.418 0-8 2.239-8 5v1h16v-1c0-2.761-3.582-5-8-5z"
                    />
                  </svg>
                </button>
                <!-- Manage Cameras -->
                <button
                  class="overlay-icon-btn"
                  @click="manageCameras"
                  aria-label="Manage cameras"
                  title="Manage cameras"
                >
                  <p>Manage Cameras</p>
                  <svg
                    viewBox="0 0 24 24"
                    width="18"
                    height="18"
                    aria-hidden="true"
                  >
                    <path
                      fill="currentColor"
                      d="M20 22h-6q-.425 0-.713-.288T13 21v-6q0-.425.288-.713T14 14h6q.425 0 .713.288T21 15v2l1.575-1.575q.125-.125.275-.063t.15.238v4.8q0 .175-.15.238t-.275-.063L21 19v2q0 .425-.288.713T20 22Zm-9 0h-.875q-.375 0-.65-.25t-.325-.625l-.3-2.325q-.325-.125-.613-.3t-.562-.375l-2.175.9q-.35.15-.7.038t-.55-.438L2.4 15.4q-.2-.325-.125-.7t.375-.6l1.875-1.425Q4.5 12.5 4.5 12.337v-.674q0-.163.025-.338L2.65 9.9q-.3-.225-.375-.6t.125-.7l1.85-3.225q.2-.325.55-.438t.7.038l2.175.9q.275-.2.575-.375t.6-.3l.3-2.325q.05-.375.325-.625t.65-.25h3.75q.375 0 .65.25t.325.625l.3 2.325q.325.125.613.3t.562.375l2.175-.9q.35-.15.7-.038t.55.438L21.6 8.6q.2.325.125.7t-.375.6l-1.875 1.425q.025.175.025.338V12h-3.95q0-1.45-1.025-2.475T12.05 8.5q-1.45 0-2.475 1.025T8.55 12q0 1.2.675 2.1T11 15.35V22Z"
                    />
                  </svg>
                </button>
                <!-- Lighthouse shortcut -->
                <a href="http://tc3.dasvideolab.cloud:3000">
                  <button
                    class="overlay-icon-btn"
                    aria-label="Lighthouse"
                    title="Lighthouse"
                  >
                    <p>Lighthouse</p>
                    <svg
                      xmlns="http://www.w3.org/2000/svg"
                      xmlns:xlink="http://www.w3.org/1999/xlink"
                      fill="currentColor"
                      height="800px"
                      width="800px"
                      version="1.1"
                      id="Capa_1"
                      viewBox="0 0 288 288"
                      xml:space="preserve"
                    >
                      <path
                        d="M65.609,186.235L76.687,97h70.46l4.41,35.522L65.609,186.235z M178.917,273h-9.989c0.001-0.056,0.008-0.111,0.008-0.167  c0-0.507-0.05-1.003-0.146-1.481l-12.467-100.429l-95.785,59.861c-0.16,0.1-0.328,0.175-0.492,0.261l-5.073,40.865  c-0.045,0.365-0.054,0.729-0.045,1.091H44.916c-4.143,0-7.5,3.357-7.5,7.5s3.357,7.5,7.5,7.5h134c4.143,0,7.5-3.357,7.5-7.5  S183.059,273,178.917,273z M148.917,69h-5.245l-1.56-12H117.25c-0.736,0-1.438-0.399-2.076-0.669  c-0.638-0.27-1.213-0.785-1.695-1.268c-0.724-0.724-1.407-1.717-1.62-2.759c-0.071-0.347-0.275-0.738-0.275-1.106v-0.031v-5.833l0,0  c0-0.368,0.204-0.728,0.275-1.075c0.355-1.736,1.637-3.165,3.232-3.839c0.638-0.27,1.423-0.419,2.159-0.419h22.802l-1.198-9.598  c2.682-1.143,4.562-3.802,4.562-6.902c0-3.941-3.041-7.165-6.903-7.47C133.209,6.539,124.152,0,113.705,0h-3.576  C99.692,0,90.644,6.526,87.332,16h-0.415c-4.143,0-7.5,3.357-7.5,7.5c0,3.457,2.341,6.359,5.522,7.228L80.163,69h-6.246  c-4.143,0-7.5,3.357-7.5,7.5c0,2.176,0.932,4.13,2.413,5.5h85.174c1.48-1.37,2.413-3.324,2.413-5.5  C156.417,72.357,153.059,69,148.917,69z M248.068,26.451c-1.699-1.427-3.861-2.026-6.044-1.638l-91.958,16.302  c-3.581,0.635-6.171,3.747-6.171,7.385s2.621,6.75,6.202,7.385l92.005,16.302c0.436,0.077,0.876,0.115,1.311,0.115  c1.748,0,3.293-0.611,4.653-1.753c1.7-1.425,2.516-3.529,2.516-5.747V32.198C250.583,29.98,249.769,27.876,248.068,26.451z"
                      />
                    </svg>
                  </button>
                </a>
                <!-- Sign out -->
                <button
                  class="overlay-icon-btn danger"
                  @click="logout"
                  aria-label="Sign out"
                  title="Sign out"
                >
                  <p>Sign Out</p>
                  <svg
                    viewBox="0 0 24 24"
                    width="18"
                    height="18"
                    aria-hidden="true"
                  >
                    <path
                      fill="currentColor"
                      d="m17 8l-1.4 1.4l1.6 1.6H9v2h8.2l-1.6 1.6L17 16l4-4l-4-4M5 5h7V3H5c-1.1 0-2 .9-2 2v14c0 1.1.9 2 2 2h7v-2H5V5Z"
                    />
                  </svg>
                </button>
              </div>
            </Transition>

            <!-- User info dropdown -->
            <div
              v-if="showUserInfo"
              class="user-info-dropdown"
              @click.stop
              ref="userInfoDiv"
            >
              <div class="row">
                <span class="label">User</span>
                <span class="value" :title="userName">{{ userName }}</span>
              </div>
              <div class="row">
                <span class="label">Org</span>
                <span class="value" :title="organisation">{{
                  organisation
                }}</span>
              </div>
              <div class="row">
                <span class="label">Role</span>
                <span class="value" :title="orgRole">{{ orgRole }}</span>
              </div>

              <template
                v-if="$store.getters['googleAuth/authenticatedWithGoogle']"
              >
                <button
                  class="google-btn"
                  @click="$store.dispatch('googleAuth/signOutGoogle')"
                >
                  <img
                    class="google-logo"
                    src="https://www.gstatic.com/firebasejs/ui/2.0.0/images/auth/google.svg"
                    alt="Google logo"
                  />
                  Sign out of Google
                </button>
              </template>
              <template v-else>
                <button
                  class="google-btn"
                  @click="$store.dispatch('googleAuth/signInWithGoogle')"
                >
                  <img
                    class="google-logo"
                    src="https://www.gstatic.com/firebasejs/ui/2.0.0/images/auth/google.svg"
                    alt="Google logo"
                  />
                  Sign in with Google
                </button>
              </template>

              <template
                v-if="$store.getters['googleAuth/authenticatedWithMicrosoft']"
              >
                <button
                  class="microsoft-btn"
                  @click="$store.dispatch('googleAuth/signOutMicrosoft')"
                >
                  <img
                    class="microsoft-logo"
                    src="https://www.gstatic.com/firebasejs/ui/2.0.0/images/auth/microsoft.svg"
                    alt="Microsoft logo"
                  />
                  Sign out of Microsoft
                </button>
              </template>
              <template v-else>
                <button
                  class="microsoft-btn"
                  @click="$store.dispatch('googleAuth/signInWithMicrosoft')"
                >
                  <img
                    class="microsoft-logo"
                    src="https://www.gstatic.com/firebasejs/ui/2.0.0/images/auth/microsoft.svg"
                    alt="Microsoft logo"
                  />
                  Sign in with Microsoft
                </button>
              </template>
              <div class="row footer">
                <span class="value">{{ version }} </span>
                <svg
                  @click.stop="dumpLogs"
                  style="cursor: pointer; color: var(--text-faint)"
                  v-if="isSoldelAdmin"
                  xmlns="http://www.w3.org/2000/svg"
                  width="16"
                  height="16"
                  fill="currentColor"
                  class="bi bi-bug"
                  viewBox="0 0 16 16"
                >
                  <path
                    d="M4.355.522a.5.5 0 0 1 .623.333l.291.956A5 5 0 0 1 8 1c1.007 0 1.946.298 2.731.811l.29-.956a.5.5 0 1 1 .957.29l-.41 1.352A5 5 0 0 1 13 6h.5a.5.5 0 0 0 .5-.5V5a.5.5 0 0 1 1 0v.5A1.5 1.5 0 0 1 13.5 7H13v1h1.5a.5.5 0 0 1 0 1H13v1h.5a1.5 1.5 0 0 1 1.5 1.5v.5a.5.5 0 1 1-1 0v-.5a.5.5 0 0 0-.5-.5H13a5 5 0 0 1-10 0h-.5a.5.5 0 0 0-.5.5v.5a.5.5 0 1 1-1 0v-.5A1.5 1.5 0 0 1 2.5 10H3V9H1.5a.5.5 0 0 1 0-1H3V7h-.5A1.5 1.5 0 0 1 1 5.5V5a.5.5 0 0 1 1 0v.5a.5.5 0 0 0 .5.5H3c0-1.364.547-2.601 1.432-3.503l-.41-1.352a.5.5 0 0 1 .333-.623M4 7v4a4 4 0 0 0 3.5 3.97V7zm4.5 0v7.97A4 4 0 0 0 12 11V7zM12 6a4 4 0 0 0-1.334-2.982A3.98 3.98 0 0 0 8 2a3.98 3.98 0 0 0-2.667 1.018A4 4 0 0 0 4 6z"
                  />
                </svg>
                <span class="value">{{ time }}</span>
              </div>
            </div>
            <!-- Help menu dropdown -->
            <div
              v-if="showHelp"
              class="help-dropdown"
              @click.stop
              ref="helpDropdown"
              role="dialog"
              aria-label="Keyboard shortcuts"
              tabindex="-1"
            >
              <button
                type="button"
                class="help-close"
                aria-label="Close help"
                title="Close (Esc)"
                @click="closeHelp"
              ></button>
              <h4>Playback & Navigation</h4>
              <ul class="shortcut-grid">
                <li>
                  <span class="keys">
                    <kbd>A</kbd> / <kbd>J</kbd> / <kbd>◀</kbd> /
                    <kbd>Numpad 4</kbd>
                  </span>

                  <span class="desc">Rewind 20s</span>
                </li>
                <li>
                  <span class="keys">
                    <kbd>S</kbd> / <kbd>K</kbd> / <kbd>Space</kbd> /
                    <kbd>Numpad 5</kbd>
                  </span>

                  <span class="desc">Play / Pause</span>
                </li>
                <li>
                  <span class="keys">
                    <kbd>D</kbd> / <kbd>L</kbd> / <kbd>▶</kbd> /
                    <kbd>Numpad 6</kbd>
                  </span>

                  <span class="desc">Fast-forward 20s</span>
                </li>
                <li>
                  <span class="keys"><kbd>F</kbd> / <kbd>Numpad *</kbd></span>
                  <span class="desc">Toggle Fullscreen</span>
                </li>
              </ul>
              <details>
                <summary>Advanced Shortcuts</summary>
                <h4>Events</h4>
                <ul class="shortcut-grid">
                  <li>
                    <span class="keys">
                      <kbd>Q</kbd> / <kbd>▼</kbd> / <kbd>Numpad 7</kbd>
                    </span>

                    <span class="desc">Previous event</span>
                  </li>
                  <li>
                    <span class="keys"><kbd>W</kbd> / <kbd>Numpad 8</kbd></span>
                    <span class="desc">Capture JPEG (current frame)</span>
                  </li>
                  <li>
                    <span class="keys">
                      <kbd>E</kbd> / <kbd>▲</kbd> / <kbd>Numpad 9</kbd>
                    </span>
                    <span class="desc">Next event</span>
                  </li>
                  <li>
                    <span class="keys"><kbd>R</kbd> / <kbd>Numpad /</kbd></span>
                    <span class="desc">Open Clip Maker</span>
                  </li>
                </ul>

                <h4>Time & Modes</h4>
                <ul class="shortcut-grid">
                  <li>
                    <span class="keys"><kbd>Z</kbd> / <kbd>Numpad 1</kbd></span>
                    <span class="desc">Remember current time</span>
                  </li>
                  <li>
                    <span class="keys"><kbd>X</kbd> / <kbd>Numpad 2</kbd></span>
                    <span class="desc">Live View mode</span>
                  </li>
                  <li>
                    <span class="keys"><kbd>C</kbd> / <kbd>Numpad 3</kbd></span>
                    <span class="desc">Play from remembered time</span>
                  </li>
                  <li>
                    <span class="keys"><kbd>V</kbd> / <kbd>Numpad 0</kbd></span>
                    <span class="desc">Open time picker</span>
                  </li>
                </ul>
                <template v-if="isSoldelAdmin">
                  <h4>Page Toggles</h4>
                  <ul class="shortcut-grid">
                    <li>
                      <span class="keys"><kbd>H</kbd></span>
                      <span class="desc">Cycle Holywood modes</span>
                    </li>
                    <li>
                      <span class="keys"><kbd>I</kbd></span>
                      <span class="desc">Toggle I-frame control</span>
                    </li>
                    <li>
                      <span class="keys"><kbd>T</kbd></span>
                      <span class="desc">Cycle internal event types</span>
                    </li>
                  </ul>
                </template>
              </details>

              <p class="hint">
                Tip: Some keyboards label numpad keys as <em>Num*</em>,
                <em>Num/</em>, etc.
              </p>
            </div>
          </div>
        </div>
        <TimelineScrubber
          v-if="showTimelineScrubber"
          v-model="scrubTs"
          :windowMinutes="timelineWindowMinutes"
          :majorEveryMin="timelineMajorEvery"
          :minorEveryMin="timelineMinorEvery"
          :debounceMs="timelineDebounceMs"
          class="timeline-mount"
          @seek="onScrubSeek"
          :events="timelineEvents"
        />
      </div>
      <div
        class="row-resizer"
        :class="{ dragging: rowDragging }"
        role="separator"
        aria-orientation="horizontal"
        tabindex="0"
        @mousedown="startRowResize"
        @touchstart.prevent="startRowResize"
        title="Drag to resize"
      >
        <div
          class="row-handle"
          aria-hidden="true"
          @pointerdown.stop.prevent="startRowResize"
          @mousedown.stop.prevent="startRowResize"
          @touchstart.stop.prevent="startRowResize"
        >
          <span class="row-handle-dots" aria-hidden="true">⋯</span>
        </div>
      </div>
      <div
        class="cameras"
        :style="{
          height: `calc(100% - ${livePx}px - var(--resizer-thickness))`,
        }"
      >
        <div class="section-header">
          <p class="title">Sites</p>
          <div class="device-tools">
            <div class="device-filter">
              <input
                ref="deviceSearch"
                type="search"
                class="device-search"
                v-model.trim="filterQuery"
                placeholder="Search sites/cameras…"
                aria-label="Filter devices and cameras"
                @keydown.stop
                @keydown.escape.prevent="clearDeviceSearch"
              />
              <button
                v-show="filterQuery"
                class="clear-btn"
                type="button"
                aria-label="Clear search"
                title="Clear"
                @click="clearDeviceSearch"
              ></button>
            </div>
            <div class="device-jump">
              <CustomSelect
                v-model="selectedDeviceName"
                :options="deviceOptions"
                placeholder="Jump to site..."
                idPrefix="device-jump"
                width="155px"
                @change="scrollToDevice"
              />
            </div>
          </div>
        </div>

        <div class="device-list" ref="deviceList">
          <LoadingWheel v-if="loading == true" class="loading-indicator" />
          <template v-else>
            <div
              v-if="filteredDevices.length === 0"
              class="no-cameras"
              style="margin-top: 0.5rem"
            >
              No matching sites/cameras.
            </div>
            <div
              v-else
              class="device"
              v-for="(device, deviceIndex) in filteredDevices"
              :key="'device-' + deviceIndex"
              :ref="(el) => setDeviceRef(device.name, el)"
            >
              <p>{{ device.name }}</p>
              <div class="device-cameras" style="--cols: 4" :ref="observeGrid">
                <template v-if="device.cameras && device.cameras.length == 0">
                  <p class="no-cameras">No cameras found.</p>
                </template>
                <template v-else>
                  <div
                    class="camera"
                    v-for="camera in device.cameras"
                    :key="camera.id"
                    @click="viewLiveStream(camera)"
                  >
                    <div
                      class="statusIndicator"
                      v-if="isSoldelAdmin"
                      :class="getLighthouseStatus(camera.id)"
                    >
                      {{ getLighthouseStatusText(camera.id) }}
                    </div>
                    <div
                      class="cameraImage"
                      :class="{
                        'current-camera': isActive(camera),
                      }"
                    >
                      <img
                        class="hidden"
                        v-bind:key="camera.id"
                        v-if="camera.state === 'active'"
                        v-shown="loadImage"
                        :data-cam-id="camera.id"
                        alt="camera"
                      />
                      <img
                        v-else
                        src="@/assets/videoOff.svg"
                        alt="camera off"
                      />
                    </div>
                    <marquee-display class="marquee">
                      <p :title="'ID: ' + camera.id">{{ camera.name }}</p>
                    </marquee-display>
                  </div>
                  <!-- <div v-if="device.cameras[0]" class="camera" @click="viewLibPeer(device)">
                            <div class="cameraImage">
                              <img src="@/assets/videoIcon.svg" alt="Lib Peer" style="scale:0.6;"/>
                            </div>
                            <p>ESP-32</p>
                          </div>
                            <div v-if="device.cameras[0]" class="camera" @click="viewWebConsole(device)">
                              <div class="cameraImage">
                                <img src="@/assets/webconsole.svg" alt="Web Console" />
                              </div>
                              <p>Web Console</p>
                          </div> -->
                </template>
              </div>
            </div>
          </template>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
  import TimelineScrubber from '@/components/TimelineScrubber.vue';

  import EventBus from '@/utils/EventBus';
  import aws_info from '@/utils/websocket';
  import { Auth } from 'aws-amplify';
  import screenfull from 'screenfull';

  import LiveVideo from '@/components/LiveVideo.vue';
  import LoadingWheel from '@/components/LoadingWheel.vue';

  import activeCamIcon from '@/assets/videoIcon.svg';

  import { get } from 'idb-keyval';

  import { toDatetimeLocal } from '@/utils/DateTime';

  import { createMP4Clip } from '@/utils/messaging/MediaMakerMessages';

  import DCv2 from '@/utils/DCv2';

  import CustomSelect from '@/components/CustomSelect.vue';
  import LibPeer from '@/components/LibPeer.vue';
  import MarqueeDisplay from '@/components/MarqueeDisplay.vue';
  import TempestLogo from '@/components/TempestLogo.vue';
  import WebConsole from '@/components/WebConsole.vue';
  import { nextTick } from 'vue';

  export default {
    name: 'HomeView',
    components: {
      TimelineScrubber,
      LiveVideo,
      LoadingWheel,
      WebConsole,
      LibPeer,
      MarqueeDisplay,
      CustomSelect,
      TempestLogo,
    },
    directives: {
      shown: {
        mounted: async (el, binding, vnode) => {
          //OLD: const func = vnode.context[binding.expression];
          const func = binding.value;
          if (typeof func === 'function') func(el, vnode.key);
        },
      },
    },
    data() {
      return {
        // Timeline scrubber
        showTimelineScrubber: true,
        scrubTs: Date.now(),
        timelineWindowMinutes: 60,
        timelineMajorEvery: 15,
        timelineMinorEvery: 5,
        timelineDebounceMs: 1000,
        lastAutoScrollEvent: null,
        changeDateDebounce: null,
        events: Array.from(Array(10).keys()),
        devices: [],
        camera: null,
        consoleCamId: null,
        libpeerCamId: null,
        loading: true,
        dayOffset: 0,
        cameraLoading: true,
        showMovieModal: false,
        showOutdatedBrowserModal: false,
        startTime: '',
        endTime: '',
        downloadingClip: false,
        movieClipStatus: 'not-created',
        movieClipTimer: -1,
        dcv2: null,
        sessionId: '',
        clipModalMode: 'creating',
        controlIFrameMode: false,
        holywoodMode: 3,
        showUserInfo: false,
        showHelp: false,
        exportMenuOpen: false,
        exportChoice: 'download',
        internalEventTypes: 0,
        selectedDeviceName: null,
        deviceRefs: Object.create(null),
        filterQuery: '',
        livePx: 0,
        minLive: 160,
        minCams: 180,
        rowMoveHandler: null,
        rowUpHandler: null,
        rowDragging: false,
        menuOpen: false,
        utilsMenuCloseTimer: null,
        gridObservers: new WeakMap(),
        lastVideoDateEmitted: 0,
        newEventsIncoming: null,
        lighthouseStats: false,
      };
    },
    mounted() {
      // Initialize scrubber to current playhead
      try {
        this.scrubTs = this.liveHead;
      } catch {
        /* Ignore */
      }
      this.checkOutdatedBrowser();

      EventBus.$on('receivedMessageEvent', this.receivedMessageEvent);
      aws_info.list();
      this.loading = true;
      window.get_file = this.get_file;

      window.addEventListener('keydown', this.handleKeys);
      document.addEventListener('click', this.handleGlobalClick);

      this.$nextTick(() => {
        const pane = this.$refs.rightPane;
        if (!pane) return;
        const total = pane.clientHeight || window.innerHeight;
        const saved = Number(localStorage.getItem('liveSplitPx'));
        const initial =
          Number.isFinite(saved) && saved ? saved : Math.round(total * 0.5);
        this.livePx = Math.max(
          this.minLive,
          Math.min(initial, total - this.minCams)
        );
        if (this.$refs.liveVideo && this.$refs.liveVideo.handleResize)
          this.$refs.liveVideo.handleResize();
        window.addEventListener('resize', this.onWindowResize);
      });
      this.fetchLighthouseStats();
    },
    beforeUnmount() {
      EventBus.$off('receivedMessageEvent', this.receivedMessageEvent);
      if (this.dcv2 !== null) {
        if (this.dcv2.isConnected()) this.dcv2.disconnect();
      }

      window.removeEventListener('keydown', this.handleKeys);
      document.removeEventListener('click', this.handleGlobalClick);
      window.removeEventListener('resize', this.onWindowResize);
      // Disconnect any ResizeObservers we created
      this._gridObservers &&
        [...this._gridObservers.values()].forEach((ro) => {
          try {
            ro.disconnect();
          } catch {
            /* Ignore */
          }
        });
    },
    methods: {
      onScrubSeek(ts) {
        const now = new Date();
        const todayMid = new Date(
          now.getFullYear(),
          now.getMonth(),
          now.getDate()
        ).getTime();
        const t = new Date(ts);
        const tsMid = new Date(
          t.getFullYear(),
          t.getMonth(),
          t.getDate()
        ).getTime();
        const offset = Math.max(0, Math.round((todayMid - tsMid) / 86400000));
        if (offset !== this.dayOffset) this.dayOffset = offset;
        this.performSeek(ts);
      },
      performSeek(ts) {
        const lv = this.$refs?.liveVideo;
        if (lv) lv.seekTo(ts);
        if (Math.abs(ts - Date.now()) < 10 * 1000 || ts >= Date.now()) {
          console.debug('Scrubber seek to live');
          if (lv) lv.switchToLiveview();
        }
      },
      checkOutdatedBrowser() {
        try {
          const suppress = parseInt(
            localStorage.getItem('suppressOutdatedBrowserWarning'),
            10
          );
          if (Number.isFinite(suppress) & (Date.now() < suppress)) return;

          const ua = navigator.userAgent || '';
          const isIOS = /iP(hone|ad|od)/.test(ua);
          const safariMatch = ua.match(/Version\/(\d+)\.(\d+).*Safari/);
          const chromeMatch = ua.match(/Chrom(e|ium)\/(\d+)/);
          const firefoxMatch = ua.match(/Firefox\/(\d+)/);

          let outdatedUA = false;
          if (safariMatch) {
            const major = parseInt(safariMatch[1], 10);
            if (!Number.isNaN(major)) outdatedUA = outdatedUA || major < 17;
          }
          if (isIOS) {
            const m = ua.match(/OS (\d+)_/);
            if (m) outdatedUA = outdatedUA || parseInt(m[1], 10) < 16;
          }
          if (chromeMatch) {
            const major = parseInt(chromeMatch[2], 10);
            if (!Number.isNaN(major)) outdatedUA = outdatedUA || major < 115;
          }
          if (firefoxMatch) {
            const major = parseInt(firefoxMatch[1], 10);
            if (!Number.isNaN(major)) outdatedUA = outdatedUA || major < 115;
          }

          // Feature checks for critical modern capabilities
          const supportsHas =
            (window.CSS && CSS.supports && CSS.supports('selector(:has(*))')) ||
            false;
          const hasResizeObserver = 'ResizeObserver' in window;
          const hasPromiseFinally = !!(
            window.Promise &&
            Promise.prototype &&
            typeof Promise.prototype.finally === 'function'
          );

          const failures = [
            supportsHas ? null : ':has() selector',
            hasResizeObserver ? null : 'ResizeObserver',
            hasPromiseFinally ? null : 'Promise.finally',
          ].filter(Boolean);

          const outdated = outdatedUA || failures.length;

          if (outdated) {
            this.showOutdatedBrowserModal = true;
          }
        } catch (e) {
          // If the check throws for any reason, fail-safe to not block users.
          console.warn('Browser compatibility check failed:', e);
        }
      },
      dismissOutdatedBrowserModal(dontShowAgain = false) {
        if (dontShowAgain) {
          try {
            localStorage.setItem(
              'suppressOutdatedBrowserWarning',
              String(Date.now() + 60 * 60 * 1000)
            );
          } catch (e) {
            /* Ignore */
          }
        }
        this.showOutdatedBrowserModal = false;
      },

      toggleFullscreen() {
        if (!screenfull?.isEnabled) return;
        if (screenfull.isFullscreen) screenfull.exit();
        else screenfull.request(this.$refs.livestreamFsTarget);
      },
      nextCam() {
        const curId = this.camera?.id;
        if (curId == null) return;

        const idEq = (a, b) => String(a) === String(b);

        const filtered = this.filteredDevices.flatMap((g) => g.cameras || []);
        const dev = (this.devices || []).find(
          (d) =>
            Array.isArray(d.cameras) && d.cameras.some((c) => idEq(c.id, curId))
        );
        const devCams = dev?.cameras ?? [];
        const global = (this.devices || []).flatMap((d) => d.cameras || []);

        const list =
          (filtered.length && filtered) ||
          (devCams.length && devCams) ||
          global;

        if (!Array.isArray(list) || !list.length) return;

        const len = list.length;
        const idx = list.findIndex((c) => idEq(c.id, curId));
        const next = list[((idx >= 0 ? idx : -1) + 1) % len] || list[0];

        if (next) this.viewLiveStream(next);
      },
      prevCam() {
        const curId = this.camera?.id;
        if (curId == null) return;

        const idEq = (a, b) => String(a) === String(b);

        const filtered = this.filteredDevices.flatMap((g) => g.cameras || []);
        const dev = (this.devices || []).find(
          (d) =>
            Array.isArray(d.cameras) && d.cameras.some((c) => idEq(c.id, curId))
        );
        const devCams = dev?.cameras ?? [];
        const global = (this.devices || []).flatMap((d) => d.cameras || []);

        const list =
          (filtered.length && filtered) ||
          (devCams.length && devCams) ||
          global;

        if (!Array.isArray(list) || !list.length) return;

        const len = list.length;
        const idx = list.findIndex((c) => idEq(c.id, curId));
        const prev =
          list[((idx >= 0 ? idx : 0) - 1 + len) % len] || list[len - 1];

        if (prev) this.viewLiveStream(prev);
      },
      handleKeys(event) {
        let code = event.code;
        if (
          event.target.isContentEditable ||
          ['INPUT', 'TEXTAREA', 'SELECT'].includes(event.target.tagName)
        )
          return;
        if (event.repeat) return;

        const isMod = event.metaKey || event.ctrlKey;
        const isSlash =
          event.code === 'Slash' || event.key === '/' || event.key === '?';

        if (isMod && isSlash) {
          event.preventDefault();
          this.showHelp = !this.showHelp;
          this.exportMenuOpen = false;
          this.showUserInfo = false;

          this.$nextTick(() => this.$refs.helpDropdown?.focus?.());
          return;
        }

        switch (code) {
          case 'KeyH': // Enable / disable object detected rendering
            if (!this.isSoldelAdmin) break;
            this.holywoodMode++;
            if (this.holywoodMode > 3) this.holywoodMode = 0;
            break;

          case 'KeyI':
            if (!this.isSoldelAdmin) break;
            this.controlIFrameMode = !this.controlIFrameMode;

            this.$store?.dispatch?.('addNotification', {
              type: `${this.controlIFrameMode ? 'green' : 'red'}`,
              title: `IFrame Mode`,
              comment: `${this.controlIFrameMode ? 'Enabled' : 'Disabled'}`,
            });
            break;

          case 'KeyT':
            if (!this.isSoldelAdmin) break;
            this.$store.commit('cycleEventTypes');
            this.$store?.dispatch?.('addNotification', {
              type: `green`,
              title: `Event Type`,
              comment: `Internal filter set to ${this.eventTypes}`,
            });
            console.log('event types = ', this.eventTypes);
            break;
          case 'Escape':
            this.showHelp = false;
            this.exportMenuOpen = false;
            this.showUserInfo = false;
            if (this.showMovieModal && !this.downloadingClip)
              this.closeClipMaker();
            break;
        }
      },

      viewLibPeer(device) {
        if (!device.cameras[0]) return;
        this.consoleCamId = null;
        this.camera = null;
        this.libpeerCamId = null;
        const camId = device.cameras[0].id;
        console.log('Starting lib peer for camera', camId);
        // Allow time for the old console to be removed after setting it to null
        nextTick(() => {
          this.libpeerCamId = camId;
        });
      },
      viewWebConsole(device) {
        if (!device.cameras[0]) return;
        this.consoleCamId = null;
        this.libpeerCamId = null;
        this.camera = null;
        const camId = device.cameras[0].id;
        console.log('Starting web console for camera', camId);
        // Allow time for the old console to be removed after setting it to null
        nextTick(() => {
          this.consoleCamId = camId;
        });
      },
      async receivedMessageEvent(message) {
        if (message.response == 'GetDeviceList') {
          const sortAlphaNum = (a, b) =>
            a.name.localeCompare(b.name, 'en', { numeric: true });
          this.devices = message.data.devices.sort(sortAlphaNum);
          this.loading = false;
          try {
            const lastId = localStorage.getItem('lastCamId');
            if (lastId && !this.camera) {
              const allCams = (this.devices || []).flatMap(
                (d) => d.cameras || []
              );
              const found = allCams.find(
                (c) => String(c.id) === String(lastId)
              );
              if (found) this.viewLiveStream(found);
            }
          } catch (e) {
            /* ignore errors */
          }
        }
      },
      viewLiveStream(camera) {
        if (!camera || !camera.id) return;
        if (this.camera == camera) return;
        if (this.camera && String(this.camera.id) === String(camera.id)) return;

        if (this.dcv2 !== null) {
          if (this.dcv2.isConnected()) this.disconnect();
        }

        this.closeClipMaker();

        this.consoleCamId = null;
        this.libpeerCamId = null;

        this.camera = camera;

        try {
          localStorage.setItem('lastCamId', String(camera?.id ?? ''));
        } catch (e) {
          /* ignore errors */
        }
        this.$store.dispatch('changeEvents', null);

        const date = new Date(new Date().toDateString());
        const timeZoneOffset = date.getTimezoneOffset();

        // Calculate the Unix epoch time for the start and end of the day
        const startOfDay =
          date.getTime() -
          (date.getHours() * 60 + date.getMinutes()) * 60 * 1000 -
          date.getSeconds() * 1000 -
          date.getMilliseconds() -
          timeZoneOffset * 60 * 1000;
        let endOfDay = startOfDay + (86400000 - 1);
        if (endOfDay - 1 > Date.now()) endOfDay = Date.now();
        this.$store.dispatch('changeEndTime', endOfDay);
        this.$store.dispatch('changeStartTime', startOfDay);

        this.dcv2 = new DCv2(this.camera.id);

        this.$nextTick(() => {
          const el = this.$el.querySelector(
            `.camera:has(.cameraImage.current-camera)`
          );
          el?.scrollIntoView({ behavior: 'smooth', block: 'center' });
        });
      },
      getEventStartTime(event) {
        const date = new Date(event.data.s);
        const padNum = (x) => (x > 9 ? `${x}` : `0${x}`);
        let hours = padNum(date.getHours());
        let mins = padNum(date.getMinutes());
        let seconds = padNum(date.getSeconds());
        return [hours, mins, seconds].join(':');
      },
      getEventDuration(event) {
        let start = event.data.s;
        let end = event.data.e;
        let durationMs = end - start;
        return this.formatDuration(durationMs);
      },
      eventDistanceToNow(event) {
        const start = event.data.s;
        const end = event.data.e;

        const lv = this.$refs.liveVideo;
        let playhead = 0;

        if (lv) {
          if (typeof lv?.currentDate === 'string' && lv.parseDateTime) {
            const d = lv.parseDateTime(lv.currentDate);
            if (d && Number.isFinite(d.getTime())) playhead = d.getTime();
          } else if (
            Number.isFinite(lv?.lastTimeStamp) &&
            lv.lastTimeStamp > 0
          ) {
            playhead = lv.lastTimeStamp;
          } else if (lv?.playmode === 'lv') {
            playhead = this.nowMs;
          }
        }
        if (playhead < start) return start - playhead;
        if (end < playhead) return playhead - end;
        return 0;
      },
      // ChatGPT wrote this
      formatDuration(milliseconds) {
        // Calculate the duration in various units

        // Create an array of strings to hold the duration components
        const components = [];
        // Add days to the components if applicable
        const days = Math.floor(milliseconds / (24 * 60 * 60 * 1000));
        if (days > 0) {
          components.push(`${days}d`);
          milliseconds -= days * 24 * 60 * 60 * 1000;
        }

        // Add hours to the components if applicable
        const hours = Math.floor(milliseconds / (60 * 60 * 1000));
        if (hours > 0) {
          components.push(`${hours}h`);
          milliseconds -= hours * 60 * 60 * 1000;
        }

        // Add minutes to the components if applicable
        const minutes = Math.floor(milliseconds / (60 * 1000));
        if (minutes > 0) {
          components.push(`${minutes}m`);
          milliseconds -= minutes * 60 * 1000;
        }

        // Add seconds to the components if applicable
        const seconds = Math.floor(milliseconds / 1000);
        if (seconds > 0) {
          components.push(`${seconds % 60}s`);
        }

        // If no components were added, return "0s"
        if (components.length === 0) {
          return '0s';
        }

        // Combine the components into a single string
        if (components.length === 1) {
          return components[0];
        } else {
          return components.join(' ');
        }
      },
      eventType(event) {
        switch (event.data.t.s) {
          case 'e':
            return 'external';
          case 'm':
            return 'motion';
          case 'v':
            return 'car';
          case 'p':
            return 'human';
          default:
            return 'generic';
        }
      },
      eventIcon(event) {
        let eventType = this.eventType(event);
        return this.eventTypeIcon(eventType);
      },
      eventTypeIcon(eventType) {
        switch (eventType) {
          case 'human':
            eventType = 'person-walking';
            break;
          case 'car':
            eventType = 'car';
            break;
          case 'external':
            eventType = 'external';
            break;
          case 'motion':
            eventType = 'motion';
            break;
          default:
            eventType = 'generic';
            break;
        }

        let images = require.context('../assets/', false, /\.svg$/);

        return images('./' + eventType + '.svg');
      },
      /**
       * Determine secondary (sub) event type for overlay icon.
       * For now, only consider 'p' (person) and 'v' (vehicle).
       * Returns one of: 'human', 'car', or null if no secondary type.
       */
      subEventType(event) {
        const primary =
          (event && event.data && event.data.t && event.data.t.s) || null;
        const c = (event && event.data && event.data.c) || {};
        if (!primary || !c || typeof c !== 'object') return null;

        // If primary is 'p' and 'v' exists in c, show car as sub; if primary is 'v' and 'p' exists, show human.
        if (primary === 'p') {
          if (Object.prototype.hasOwnProperty.call(c, 'v')) return 'car';
          return null;
        }
        if (primary === 'v') {
          if (Object.prototype.hasOwnProperty.call(c, 'p')) return 'human';
          return null;
        }
        // Other primary types: for now only show a sub if either p or v exists (and is not primary)
        if (Object.prototype.hasOwnProperty.call(c, 'p')) return 'human';
        if (Object.prototype.hasOwnProperty.call(c, 'v')) return 'car';
        return 'person';
      },
      /**
       * Resolve the sub icon href (same format as eventTypeIcon returns).
       */
      subEventIcon(event) {
        const t = this.subEventType(event);
        return t ? this.eventTypeIcon(t) : null;
      },
      eventLabel(event) {
        let label = this.eventType(event);
        return this.getLabel(label);
      },
      subEventLabel(event) {
        let label = this.subEventType(event);
        return label ? this.getLabel(label) : null;
      },
      getLabel(label) {
        switch (label) {
          case 'human':
            label = 'Person';
            break;
          case 'car':
            label = 'Car';
            break;
          case 'external':
            label = 'External';
            break;
          case 'motion':
            label = 'Motion';
            break;
          default:
            label = 'Generic';
            break;
        }

        return label;
      },
      startEventPlayback(event) {
        if (event.data.s === 'No events available') {
          return;
        }
        this.$store.dispatch('selectEvent', event.data.s);
        setTimeout(() => {
          this.$store.dispatch('selectEvent', null);
        }, 100);
      },

      offsetInc() {
        this.dayOffset += 1;
      },
      offsetDec() {
        if (this.dayOffset > 0) this.dayOffset -= 1;
      },
      offsetReset() {
        this.dayOffset = 0;
      },

      changeDate() {
        this.$store.dispatch('changeEvents', null);

        const day = new Date(this.eventDate);
        const startLocal = new Date(day);
        startLocal.setHours(0, 0, 0, 0);
        const startOfDay = startLocal.getTime();

        const endLocal = new Date(day);
        endLocal.setHours(23, 59, 59, 999);
        const endOfDay = Math.min(endLocal.getTime(), Date.now());

        this.$store.dispatch('changeEndTime', endOfDay);
        this.$store.dispatch('changeStartTime', startOfDay);
      },
      setCameraLoading(value) {
        this.cameraLoading = value;
      },
      updateEvents(date) {
        const date1 = new Date(date.toDateString()); // start date
        const date2 = new Date(new Date().toDateString()); // end date

        const diffInMilliseconds = date2 - date1;
        const diffInDays = Math.round(
          Math.abs(diffInMilliseconds) / (1000 * 60 * 60 * 24)
        ); // rounding the result to the nearest integer
        this.dayOffset = diffInDays;
      },
      lvPlaymode(value) {
        if (value == 'lv') {
          this.offsetReset();
        }
      },
      displayDate() {
        return new Intl.DateTimeFormat('en-GB', {
          weekday: 'short',
          day: '2-digit',
          month: 'long',
          year: 'numeric',
        })
          .format(this.eventDate)
          .replaceAll(',', '');
      },
      logout() {
        this.$router.push({ name: 'logout' });
      },
      manageCameras() {
        this.$router.push({ name: 'manage-cameras' });
      },
      async loadImage(element, id) {
        console.log('Load Image');
        const image = await this.getImage(id);
        if (image === null || image === undefined || image.length < 10) {
          let images = require.context('../assets/', false, /\.svg$/);
          element.setAttribute('src', images('./' + 'videoIcon' + '.svg'));
          element.setAttribute('class', 'preview fallback');
        } else {
          element.setAttribute('src', image);
          const imageClass = (await this.hasImage(id)) ? 'preview' : '';
          element.setAttribute('class', imageClass);
        }
      },
      async getImage(id) {
        const image = await get(`camerapreview.${id}`); // localStorage.getItem(`camerapreview.${id}`);
        return image != null || image !== undefined ? image : activeCamIcon;
      },
      async hasImage(id) {
        return (await get(`camerapreview.${id}`)) != null;
      },
      parseDateTime(str) {
        let data = str.split(' ');
        let date = data[0].split('.').reverse().join('/');
        let time = data[1];
        return new Date(`${date} ${time} GMT`);
      },
      openClipMakerCurrentTime(currentDate) {
        let date = this.parseDateTime(currentDate);
        let time = date.getTime();

        if (Date.now() - time < 5000) {
          let endDate = new Date(Date.now());
          let startDate = new Date(Date.now() - 10000);

          this.startTime = toDatetimeLocal(startDate);
          this.endTime = toDatetimeLocal(endDate);
        } else {
          let date = this.parseDateTime(currentDate);

          let startDate = new Date(date.getTime() - 5000);
          let endDate = new Date(date.getTime() + 5000);

          this.startTime = toDatetimeLocal(startDate);
          this.endTime = toDatetimeLocal(endDate);
        }

        this.clipModalMode = 'creating';
        this.showMovieModal = true;
      },
      openClipMaker(event) {
        let startDate = new Date(event.data.s);
        let endDate = new Date(event.data.e);

        if (endDate < startDate.getTime() + 4000) {
          endDate = new Date(startDate.getTime() + 4000);
        }

        this.startTime = toDatetimeLocal(startDate);
        this.endTime = toDatetimeLocal(endDate);

        this.clipModalMode = 'creating';
        this.showMovieModal = true;
      },
      closeClipMaker() {
        this.showMovieModal = false;
        this.startTime = '';
        this.endTime = '';
        this.downloadingClip = false;

        if (this.movieClipTimer !== -1) this.stopMovieClipTimer();

        this.movieClipStatus = 'not-created';
      },
      async createClip() {
        if (this.movieClipStatus == 'complete') {
          this.downloadingClip = true;
          await window.get_file(this.sessionId + '.mp4');
          this.clipModalMode = 'downloaded';
          setTimeout(this.closeClipMaker, 2500);
          this.closeClipMaker();
          return;
        }

        if (!this.clipTimesValid) return false;

        let start = Date.parse(this.startTime);
        let end = Date.parse(this.endTime);

        if (isNaN(start) || isNaN(end)) return;

        this.downloadingClip = true;

        const message = createMP4Clip(
          this.camera.id,
          `${this.buildClipName(start)}.mp4`,
          start,
          end
        );
        this.$refs.liveVideo.sendData(message);

        this.startMovieClipTimer();

        //this.closeClipMaker()
      },

      decrementStartTime() {
        let date = new Date(this.startTime);
        date.setSeconds(date.getSeconds() - 1);
        this.startTime = toDatetimeLocal(date);
      },
      incrementStartTime() {
        let date = new Date(this.startTime);
        date.setSeconds(date.getSeconds() + 1);
        let endDate = new Date(this.endTime);

        if (endDate.getTime() - date.getTime() > 4000) {
          this.startTime = toDatetimeLocal(date);
          return;
        }

        endDate = new Date(date.getTime() + 4000);

        if (endDate.getTime() <= Date.now()) {
          this.startTime = toDatetimeLocal(date);
          this.endTime = toDatetimeLocal(endDate);
        }
      },
      decrementEndTime() {
        let startDate = new Date(this.startTime);

        let date = new Date(this.endTime);
        date.setSeconds(date.getSeconds() - 1);
        if (date.getTime() - startDate.getTime() >= 4000) {
          this.endTime = toDatetimeLocal(date);
        }
      },
      incrementEndTime() {
        let date = new Date(this.endTime);
        date.setSeconds(date.getSeconds() + 1);
        if (date.getTime() <= Date.now()) {
          this.endTime = toDatetimeLocal(date);
        }
      },
      async handleClipStatus(status) {
        if (this.movieClipStatus !== status) {
          this.movieClipStatus = status;
        }

        if (this.movieClipStatus == 'complete') {
          if (this.movieClipTimer !== -1) {
            this.stopMovieClipTimer();
            const startDate = new Date(this.startTime);
            await window.get_file(
              `${this.buildClipName(startDate.getTime())}.mp4`
            );
            this.downloadingClip = false;
            this.clipModalMode = 'downloaded';
            setTimeout(this.closeClipMaker, 2500);
          }
        }
      },
      startMovieClipTimer() {
        this.movieClipTimer = setInterval(() => {
          const startDate = new Date(this.startTime);
          const endDate = new Date(this.endTime);

          const message = createMP4Clip(
            this.camera.id,
            `${this.buildClipName(startDate.getTime())}.mp4`,
            startDate.getTime(),
            endDate.getTime()
          );
          this.$refs.liveVideo.sendData(message);
        }, 1000);
      },
      stopMovieClipTimer() {
        clearInterval(this.movieClipTimer);
        this.movieClipTimer = -1;
      },
      async get_file(filename) {
        await this.dcv2.spawn();
        await this.dcv2.doit('get', filename);
        await this.dcv2.doit('delete', filename);
        await this.dcv2.disconnect();
      },
      async doJpegCapture(ts) {
        console.log('Make JPEG process begins at', ts);
        const filename = '' + ts + '.jpg';

        // Send message to make-j
        const msg = {
          topic: 'cmd/devices/hilary-dev/data-channel-relay',
          destination: 'mmctrl',
          action: {
            command: 'make-j',
            data: {
              output: filename,
              source: this.camera.id,
              ts: {
                s: ts,
                iframesonly: false,
                qp: 0,
              },
              'rtc-osd': {
                x: 65,
                y: 95,
                height: 3,
                'time-adjust': -3600,
                text: '%Y/%m/%d %H:%M:%S',
              },
              'redaction-zones': [],
              'text-osd': [
                {
                  text: '',
                  x: 40.2,
                  y: 10,
                  height: 9,
                },
              ],
            },
          },
        };

        this.$refs.liveVideo.sendData(msg);
      },
      async mmctrl_ok(data) {
        // Initiate file download
        await this.get_file(data?.data?.output);
        console.log('Make JPEG process complete.');
      },
      setSessionId(id) {
        this.sessionId = id;
      },
      // Build a friendly clip filename based on device/camera and start time
      buildClipName(startMs) {
        const camName = this.camera?.name || this.camera?.id || '';
        const device = this.devices.find((d) =>
          d.cameras.find((c) => c.id == this.camera?.id)
        );
        const devName = device?.name || device?.id || '';
        const fileName = devName ? devName + ' ' + camName : camName;

        const start = new Date(startMs);
        const yyyy = start.getFullYear();
        const MM = String(start.getMonth() + 1).padStart(2, '0');
        const dd = String(start.getDate()).padStart(2, '0');
        const hh = String(start.getHours()).padStart(2, '0');
        const mm = String(start.getMinutes()).padStart(2, '0');
        const ss = String(start.getSeconds()).padStart(2, '0');

        const safeFileName = fileName.replaceAll(' ', '_');

        return `${safeFileName}-${yyyy}-${MM}-${dd}-${hh}-${mm}-${ss}.mp4`;
      },
      async shareToCloud(platform) {
        try {
          if (!this.clipTimesValid) return;
          this.downloadingClip = true;

          const cameraId = this.camera?.id;
          const device = this.devices.find((d) =>
            d.cameras.find((c) => c.id == cameraId)
          );
          const deviceId = device?.id || null;

          const startTime = Date.parse(this.startTime);
          const endTime = Date.parse(this.endTime);
          const clipName = this.buildClipName(startTime);

          const user = await Auth.currentAuthenticatedUser();
          const idToken = user
            .getSignInUserSession()
            .getIdToken()
            .getJwtToken();
          const accessToken = user
            .getSignInUserSession()
            .getAccessToken()
            .getJwtToken();

          const res = await fetch(
            'https://tc3.dasvideolab.cloud:8000/clip/generate',
            {
              method: 'POST',
              headers: {
                'Content-Type': 'application/json',
                Authorization: `Bearer ${accessToken}`,
              },
              body: JSON.stringify({
                accessToken: idToken,
                deviceId,
                cameraId,
                startTime,
                endTime,
                clipName,
                platform,
              }),
            }
          );

          // Non-200s should throw for uniform handling
          if (!res.ok) {
            const msg = await res.text().catch(() => '');
            throw new Error(`HTTP ${res.status}: ${msg || res.statusText}`);
          }

          // Close modal & toast
          this.closeClipMaker();
          this.$store?.dispatch?.('addNotification', {
            type: 'green',
            title: `Started upload to ${
              platform === 'google' ? 'Google Drive' : 'OneDrive'
            }`,
            comment: '',
          });
        } catch (err) {
          console.error('[Share] Failed to share event:', err);
          this.$store?.dispatch?.('addNotification', {
            type: 'red',
            title: 'Failed to start sharing',
            comment: (err?.message || '').slice(0, 180),
          });
        }
      },
      toggleExportMenu() {
        this.exportMenuOpen = !this.exportMenuOpen;
      },

      openUtilsMenu() {
        this.menuOpen = true;
        if (this.utilsMenuCloseTimer) clearTimeout(this.utilsMenuCloseTimer);
        this.utilsMenuCloseTimer = null;
      },

      closeUtilsMenu() {
        if (this.utilsMenuCloseTimer) clearTimeout(this.utilsMenuCloseTimer);
        this.utilsMenuCloseTimer = setTimeout(() => {
          if (!this.showUserInfo && !this.showHelp) this.menuOpen = false;
          this.utilsMenuCloseTimer = null;
        }, 500);
      },

      performExport() {
        this.downloadingClip = true;
        if (this.exportChoice === 'download') {
          this.createClip();
        } else {
          this.shareToCloud(this.exportChoice); // 'google' | 'microsoft'
        }
      },

      selectExport(choice) {
        this.exportChoice = choice;
        this.exportMenuOpen = false;
        // Run immediately on selection:
        this.performExport();
      },

      handleGlobalClick(e) {
        // Close dropdowns when clicking outside
        const userInfoDiv = this.$refs.userInfoDiv;
        if (
          this.showUserInfo &&
          userInfoDiv &&
          !userInfoDiv.contains(e.target)
        ) {
          this.showUserInfo = false;
        }
        const helpDropdown = this.$refs.helpDropdown;
        if (this.showHelp && helpDropdown && !helpDropdown.contains(e.target)) {
          this.showHelp = false;
        }
        if (!this.exportMenuOpen) return;
        const exportGroup = this.$refs.exportGroup;
        if (exportGroup && !exportGroup.contains(e.target)) {
          this.exportMenuOpen = false;
        }
      },
      eventFiltered(event) {
        if (!this.internalEventTypes) return true;
        if (this.camera?.id && event.camId != this.camera.id) return false;
        let label = this.eventType(event);
        let subLabel = this.subEventType(event);
        let on = true;
        if (this.personOn) on &= 'human' === subLabel || 'human' === label;
        if (this.carOn) on &= 'car' === subLabel || 'car' === label;
        return on;
      },
      togglePerson() {
        this.personOn = !this.personOn;
      },
      toggleCar() {
        this.carOn = !this.carOn;
      },
      setDeviceRef(name, el) {
        if (!name) return;
        if (el) this.deviceRefs[name] = el;
        else delete this.deviceRefs[name];
      },
      scrollToDevice(name) {
        this.selectedDeviceName = null;
        const container = this.$refs.deviceList;
        const row = this.deviceRefs?.[name];
        if (!container || !row) return;
        const top = row.offsetTop - container.offsetTop;
        container.scrollTo({ top, behavior: 'smooth' });
      },

      scrollToEvent(ts) {
        const id = `event-${ts}`;
        const el = document.getElementById(id);
        const container = this.$refs.eventList;
        if (!el || !container) return;

        // Helper: is element fully visible in container (with a small margin)
        const isVisible = () => {
          const c = container.getBoundingClientRect();
          const r = el.getBoundingClientRect();
          const margin = 8;
          return r.top >= c.top + margin && r.bottom <= c.bottom - margin;
        };

        // Perform scroll (center element vertically within container)
        const centerScroll = () => {
          const elTop = el.offsetTop - container.offsetTop;
          const target =
            elTop - (container.clientHeight / 2 - el.offsetHeight / 2);
          container.scrollTo({ top: Math.max(0, target), behavior: 'smooth' });
        };

        // If not visible, center it
        if (!isVisible()) {
          // Ensure layout is stable before measuring/scrolling
          this.$nextTick(() => {
            if (!isVisible()) centerScroll();
          });
        }
      },

      clearDeviceSearch() {
        this.filterQuery = '';
        // keep focus in the field for fast re-typing
        this.$nextTick(() => this.$refs.deviceSearch?.focus());
      },
      onWindowResize() {
        const total = this.$refs.rightPane?.clientHeight ?? 0;
        this.livePx = Math.max(
          this.minLive,
          Math.min(this.livePx, total - this.minCams)
        );
      },

      startRowResize(ev) {
        const pane = this.$refs.rightPane;
        if (!pane) return;
        if (this.rowDragging) return; // guard against re-entry

        const el = ev.currentTarget || ev.target;

        this.rowDragging = true;
        document.body.style.userSelect = 'none';
        document.body.style.cursor = 'ns-resize';

        const getPoint = (e) => (e && e.touches ? e.touches[0] : e);

        const move = (e) => {
          const pt =
            typeof PointerEvent !== 'undefined' && e instanceof PointerEvent
              ? e
              : getPoint(e);
          if (!pt) return;
          const rect = pane.getBoundingClientRect();
          const total = rect.height;
          let y = pt.clientY - rect.top;
          y = Math.max(this.minLive, Math.min(y, total - this.minCams));
          this.livePx = Math.round(y);
          if (this.$refs.liveVideo && this.$refs.liveVideo.handleResize)
            this.$refs.liveVideo.handleResize();
          if (e && e.cancelable) e.preventDefault();
        };

        const end = () => {
          if (!this.rowDragging) return;
          this.rowDragging = false;
          document.body.style.userSelect = '';
          document.body.style.cursor = '';

          window.removeEventListener('pointermove', move, true);
          window.removeEventListener('pointerup', end, true);
          window.removeEventListener('pointercancel', end, true);
          window.removeEventListener('mousemove', move, true);
          try {
            window.removeEventListener('touchmove', move, {
              capture: true,
              passive: false,
            });
          } catch {
            /* Ignore */
          }
          window.removeEventListener('mouseup', end, true);
          try {
            window.removeEventListener('touchend', end, { capture: true });
          } catch {
            /* Ignore */
          }
          window.removeEventListener('blur', end, true);
          document.removeEventListener('visibilitychange', visHandler, true);
          window.removeEventListener('mouseout', leaveWindowHandler, true);

          try {
            if (ev && ev.pointerId != null && el && el.releasePointerCapture) {
              el.releasePointerCapture(ev.pointerId);
            }
          } catch {
            /* Ignore */
          }

          try {
            localStorage.setItem('liveSplitPx', String(this.livePx));
          } catch {
            /* Ignore */
          }
        };

        const visHandler = () => {
          if (document.hidden) end();
        };

        const leaveWindowHandler = (e) => {
          if (!e.relatedTarget) end();
        };

        window.addEventListener('pointermove', move, true);
        window.addEventListener('pointerup', end, true);
        window.addEventListener('pointercancel', end, true);

        window.addEventListener('mousemove', move, true);
        try {
          window.addEventListener('touchmove', move, {
            capture: true,
            passive: false,
          });
        } catch {
          /* Ignore */
        }
        window.addEventListener('mouseup', end, true);
        try {
          window.addEventListener('touchend', end, { capture: true });
        } catch {
          /* Ignore */
        }

        window.addEventListener('blur', end, true);
        document.addEventListener('visibilitychange', visHandler, true);
        window.addEventListener('mouseout', leaveWindowHandler, true);

        try {
          if (ev && ev.pointerId != null && el && el.setPointerCapture) {
            el.setPointerCapture(ev.pointerId);
          }
        } catch {
          /* Ignore */
        }

        move(ev);
      },
      closeHelp() {
        this.showHelp = false;
        this.$nextTick(() => this.$refs.helpButton?.focus?.());
      },
      handlePreviewUpdated({ id, image }) {
        if (!id || !image) return;

        const img = this.$el.querySelector(`img[data-cam-id="${id}"]`);
        if (!img) return;

        img.setAttribute('src', image);
        img.setAttribute('class', 'preview');
      },
      isActive(cam) {
        return (this.camera?.id ?? null) === (cam?.id ?? null);
      },
      dumpLogs() {
        window.dumpConsoleTxt?.();
      },
      observeGrid(el) {
        // Called by :ref for each .device-cameras container
        if (!el) return;
        if (this.gridObservers.has(el)) return;
        const ro = new ResizeObserver(() => this.updateGridCols(el));
        this.gridObservers.set(el, ro);
        ro.observe(el);
        // Initial computation
        this.$nextTick(() => this.updateGridCols(el));
      },
      updateGridCols(el) {
        if (!el) return;
        const cardWidth = 150;
        const cols = Math.max(1, Math.floor(el.clientWidth / cardWidth));
        el.style.setProperty('--cols', String(cols));
      },
      onVideoTime() {
        this.scrubTs = this.liveHead;
      },
      updateLastVideoDateEmitted(ts) {
        this.lastVideoDateEmitted = ts;
      },
      refreshEvents() {
        // Nothing new is coming in for an old date
        if (new Date(this.liveHead).getDate() == new Date().getDate())
          this.$store.dispatch('changeEndTime', new Date().getTime());
      },
      async fetchLighthouseStats() {
        try {
          const user = await Auth.currentAuthenticatedUser();
          const accessToken = user
            .getSignInUserSession()
            .getAccessToken()
            .getJwtToken();
          const idToken = user
            .getSignInUserSession()
            .getIdToken()
            .getJwtToken();
          const params = new URLSearchParams();
          params.append('start', '-7d');
          params.append('stop', 'now()');
          params.append('command', 'uid');

          const uidsRes = await fetch(
            `http://tc3.dasvideolab.cloud:3000/api/metrics?${params}`,
            {
              method: 'GET',
              headers: {
                'Content-Type': 'application/json',
                Authorization: `Bearer ${accessToken}`,
                'X-ID-Token': idToken,
              },
            }
          );
          const uids = await uidsRes.json();
          this.lighthouseStats = uids;
        } catch {
          /* Ignore */
        }
      },
      getLighthouseStatus(id) {
        if (Object.keys(this.lighthouseIndex).length == 0) return 'loading';
        const result = this.lighthouseIndex[id];
        if (!result) return 'noData';

        const lastUpdate = new Date(result.last_update);
        const now = new Date().setMinutes(0, 0, 0); // last update will always be on the hour

        if (!lastUpdate || now - lastUpdate.getTime() > 1000 * 60 * 60 * 6)
          return 'offline';

        if (!lastUpdate || now - lastUpdate.getTime() > 1000 * 60 * 60 * 1.5)
          return 'late';

        return 'online';
      },
      getLighthouseTime(id) {
        if (Object.keys(this.lighthouseIndex).length == 0) return '';
        const result = this.lighthouseIndex[id];
        if (!result) return '';

        const lastUpdate = new Date(result.last_update);
        return lastUpdate;
      },
      getLighthouseStatusText(id) {
        const status = this.getLighthouseStatus(id);
        if (status == 'noData') return 'No Data';
        if (status == 'offline') return '!';
        return '';
      },
    },
    computed: {
      lighthouseIndex() {
        const index = {};
        if (!this.lighthouseStats) return index;

        for (const entry of Object.values(this.lighthouseStats)) {
          if (!entry || !Array.isArray(entry.mrecs)) continue;
          for (const mrec of entry.mrecs) {
            if (!mrec?.mrec_id) continue;
            index[mrec.mrec_id] = mrec;
          }
        }
        return index;
      },
      timelineEvents() {
        const list = this.eventsReversed || [];
        const filtered = this.eventFiltered
          ? list.filter((e) => this.eventFiltered(e))
          : list;
        return filtered.map((e) => ({
          s: Number(e?.data?.s),
          e: Number(e?.data?.e ?? e?.data?.s),
          iconHref: this.subEventIcon(e)
            ? [this.eventIcon(e), this.subEventIcon(e)]
            : [this.eventIcon(e)],
        }));
      },
      // Current playing event start timestamp, inferred from scrubTs and timelineEvents
      currentEventStartTs() {
        const ts = Number(this.scrubTs);
        const events = this.timelineEvents || [];
        if (!Number.isFinite(ts) || !events.length) return null;
        // Find event where ts falls within [s, e] (inclusive)
        for (let i = 0; i < events.length; i++) {
          const e = events[i];
          if (
            Number.isFinite(e.s) &&
            Number.isFinite(e.e) &&
            e.s <= ts &&
            ts <= e.e
          ) {
            return e.s;
          }
        }
        return null;
      },
      nextEventStartTs() {
        const ts = Number(this.scrubTs);
        const events = this.timelineEvents || [];
        if (!Number.isFinite(ts) || !events.length) return null;

        // Find the smallest start time strictly after ts
        let next = Infinity;
        for (let i = 0; i < events.length; i++) {
          const e = events[i];
          if (Number.isFinite(e.s) && e.s > ts && e.s < next) {
            next = e.s;
          }
        }
        return Number.isFinite(next) ? next : null;
      },

      liveHead() {
        if (this.lastVideoDateEmitted) return this.lastVideoDateEmitted;
        return Date.now();
      },
      eventTypes() {
        return this.$store.getters.eventTypes;
      },
      personOn: {
        get() {
          const mask = this.internalEventTypes ?? 3;
          return (mask & 2) !== 0;
        },
        set(v) {
          const car = this.carOn ? 1 : 0;
          const person = v ? 2 : 0;
          this.internalEventTypes = car + person;
        },
      },
      carOn: {
        get() {
          const mask = this.internalEventTypes ?? 3;
          return (mask & 1) !== 0;
        },
        set(v) {
          const car = v ? 1 : 0;
          const person = this.personOn ? 2 : 0;
          this.internalEventTypes = car + person;
        },
      },
      eventsReversed() {
        const list = this.$store.getters.eventsReverse || [];
        const camId = this.camera?.id ?? null;
        return list.map((e) => ({ ...e, camId }));
      },
      eventDate() {
        let myDate = new Date();
        myDate.setDate(myDate.getDate() - this.dayOffset);
        return myDate;
      },
      showDateButtons() {
        return (
          this.camera !== null &&
          this.camera !== undefined &&
          this.cameraLoading == false
        );
      },
      version() {
        return process.env.VUE_APP_VERSION;
      },
      time() {
        let date = new Date(process.env.VUE_APP_GIT_TIME.replace(/-/g, '/'));
        return new Intl.DateTimeFormat('en-GB', {
          day: '2-digit',
          month: '2-digit',
          year: '2-digit',
          hours: '2-digit',
          hour: 'numeric',
          minute: 'numeric',
        })
          .format(date)
          .replaceAll(',', '')
          .replaceAll('at', '');
      },
      disableClipElements() {
        return (
          this.downloadingClip == true ||
          this.movieClipStatus == 'ok' ||
          this.movieClipStatus == 'complete'
        );
      },
      clipTimesValid() {
        if (
          this.startTime == '' ||
          this.startTime == null ||
          this.startTime == undefined
        )
          return false;

        if (
          this.endTime == '' ||
          this.endTime == null ||
          this.endTime == undefined
        )
          return false;

        const start = Date.parse(this.startTime);
        const end = Date.parse(this.endTime);

        if (isNaN(start) || isNaN(end)) return false;

        let now = Date.now();

        if (now < start || now < end) return;

        if (start > end || end - 4000 < start) return false;

        return true;
      },
      mergeDevicesByBaseName() {
        const groups = {};
        const lastNumber = (s) => {
          const m = String(s ?? '').match(/(\d+)(?!.*\d)/); // last run of digits
          return m ? parseInt(m[1], 10) : null;
        };
        this.devices.forEach((dev) => {
          const fullName = String(dev.name ?? '');
          const baseName = fullName.replace(
            /( ?[0-9]+| ?[-—-] ?[0-9]+| ?\( ?[0-9]+ ?\))$/,
            ''
          );
          if (!groups[baseName])
            groups[baseName] = { name: baseName, devices: [] };
          groups[baseName].devices.push({
            name: fullName,
            caps: fullName.replace(/[^A-Z]/g, ''),
            id: String(dev.id ?? ''),
            num: lastNumber(fullName),
            cameras: Array.isArray(dev.cameras) ? dev.cameras : [],
          });
        });
        return Object.values(groups);
      },

      filteredDevices() {
        const raw = (this.filterQuery || '').trim();
        const q = raw.toLowerCase();
        if (!q) {
          // no filter -> return each base group with all its cameras
          return this.mergeDevicesByBaseName.map((g) => ({
            name: g.name,
            cameras: g.devices.flatMap((d) => d.cameras || []),
          }));
        }

        const norm = (v) => String(v ?? '').toLowerCase();
        const includes = (hay, needle) => norm(hay).includes(needle);

        // parse the last number in the query (e.g., "TED 02" -> 2)
        const numMatch = q.match(/(\d+)(?!.*\d)/);
        const qNum = numMatch ? parseInt(numMatch[1], 10) : null;

        // textual part (remove that last number - order doesn’t matter: "2 TED" works too)
        let qText = q;
        if (numMatch) {
          const i = numMatch.index;
          qText = (q.slice(0, i) + q.slice(i + numMatch[0].length))
            .replace(/\s+/g, ' ')
            .trim();
        }

        // If the query contains a number, try sub-device matching FIRST
        if (qNum !== null) {
          const results = [];
          for (const group of this.mergeDevicesByBaseName) {
            for (const d of group.devices) {
              const nameMatchesText =
                !qText ||
                includes(d.name, qText) ||
                includes(group.name, qText) ||
                includes(d.caps, qText);
              const idMatchesText = !qText || includes(d.id, qText);
              const numMatches = d.num === qNum;

              if (numMatches && (nameMatchesText || idMatchesText)) {
                // Return ONLY this sub-device, with its own name as the header
                results.push({ name: d.name, cameras: d.cameras || [] });
              }
            }
          }
          if (results.length) return results;

          // Fallback if no sub-device matched the number: camera-level filtering
          const camResults = [];
          for (const group of this.mergeDevicesByBaseName) {
            const cams = group.devices
              .flatMap((d) => d.cameras || [])
              .filter((c) => includes(c.name, q) || includes(c.id, q));
            if (cams.length)
              camResults.push({ name: group.name, cameras: cams });
          }
          if (camResults.length) return camResults;
        }

        // No number in the query
        // 1) device/base-name matches -> show whole group
        // 2) else camera-level filtering within groups
        const groups = this.mergeDevicesByBaseName
          .map((group) => {
            const deviceHit =
              includes(group.name, q) ||
              group.devices.some(
                (d) =>
                  includes(d.name, q) ||
                  includes(d.id, q) ||
                  includes(d.caps, q)
              );
            if (deviceHit) {
              return {
                name: group.name,
                cameras: group.devices.flatMap((d) => d.cameras || []),
              };
            }
            const cams = group.devices
              .flatMap((d) => d.cameras || [])
              .filter((c) => includes(c.name, q) || includes(c.id, q));
            if (cams.length) return { name: group.name, cameras: cams };
            return null;
          })
          .filter(Boolean);

        return groups;
      },
      userName() {
        if (this.$store.state.user?.name) {
          return this.$store.state.user?.name;
        }
        return '...';
      },
      organisation() {
        if (this.$store.state.user?.organisation) {
          return this.$store.state.user?.organisation;
        }
        return '...';
      },
      orgRole() {
        return this.$store.state.user?.userType ?? '...';
      },
      googleDisabled() {
        return (
          !this.$store.getters['googleAuth/authenticatedWithGoogle'] ||
          !this.clipTimesValid ||
          this.downloadingClip
        );
      },
      microsoftDisabled() {
        return (
          !this.$store.getters['googleAuth/authenticatedWithMicrosoft'] ||
          !this.clipTimesValid ||
          this.downloadingClip
        );
      },
      downloadDisabled() {
        return !this.clipTimesValid || this.downloadingClip;
      },
      exportMainDisabled() {
        if (this.exportChoice === 'download') return this.downloadDisabled;
        if (this.exportChoice === 'google') return this.googleDisabled;
        return this.microsoftDisabled;
      },
      activeCamera() {
        return this.camera;
      },
      holywoodModeString() {
        switch (this.holywoodMode) {
          case 1:
            return 'Bounding Box';
          case 2:
            return 'Confidence';
          case 3:
            return 'Bounding Box + Confidence';
        }
        return '???';
      },
      isSoldelAdmin() {
        const user = this.$store.state.user;
        const admin = user.userType.toLowerCase().includes('admin');
        const soldel = user.organisation === 'Soldel_Ltd';
        return soldel && admin;
      },
      mergeDevicesByName() {
        return this.mergeDevicesByBaseName;
      },
      deviceOptions() {
        return this.filteredDevices.map((d) => ({
          value: d.name,
          label: d.name, // or `${d.name} (${d.cameras?.length ?? 0})`
        }));
      },
      eventsReversedKey() {
        return `${this.eventsReversed}-${this.camera?.id}-${this.loading}`;
      },
    },
    watch: {
      // Keep scrubber centred on live video time when available
      liveHead() {
        this.scrubTs = this.liveHead;
      },
      // Auto-scroll when the event under the playhead changes
      scrubTs() {
        const evTs = this.currentEventStartTs ?? this.nextEventStartTs;
        if (evTs != null && evTs !== this.lastAutoScrollEvent) {
          this.lastAutoScrollEvent = evTs;
          this.$nextTick(() => this.scrollToEvent(evTs));
        }
      },
      // Re-try focusing the current event after event list changes/re-renders
      eventsReversedKey() {
        this.$nextTick(() => {
          const evTs = this.currentEventStartTs;
          if (evTs != null) this.scrollToEvent(evTs);
        });
      },

      eventsReversed: {
        handler: function () {
          if (this.newEventsIncoming) clearTimeout(this.newEventsIncoming);
          this.newEventsIncoming = setTimeout(() => {
            this.newEventsIncoming = null;
          }, 500);
        },
        deep: true,
      },
      eventDate() {
        if (this.loading) return;
        this.$store.dispatch('changeEvents', null);
        this.$store.dispatch('changeEndTime', 0);
        if (this.changeDateDebounce) clearTimeout(this.changeDateDebounce);
        this.changeDateDebounce = setTimeout(() => {
          this.changeDate();
        }, 500);
      },
      holywoodMode() {
        if (this.holywoodMode)
          this.$store?.dispatch?.('addNotification', {
            type: 'green',
            title: `Holywood Mode`,
            comment: `Now showing: ${this.holywoodModeString}`,
          });
        else
          this.$store?.dispatch?.('addNotification', {
            type: 'red',
            title: `Holywood Mode`,
            comment: `Disabled`,
          });
      },
      showHelp(newVal) {
        if (!newVal) {
          this.closeUtilsMenu();
        }
      },
      showUserInfo(newVal) {
        if (!newVal) {
          this.closeUtilsMenu();
        }
      },
    },
  };
</script>

<style scoped>
  button:not(:disabled) {
    cursor: pointer;
  }

  p,
  h1 {
    user-select: none;
  }

  .player-page {
    background: var(--bg);
    width: 100%;
    height: 100%;
  }

  .right-pane {
    display: flex;
    position: relative;
    flex-direction: column;
    align-items: stretch;
    height: 100%;
    --resizer-thickness: 2px;
  }

  .row-resizer {
    position: relative;
    cursor: row-resize;
    border-top: 4px solid var(--bg);
    border-bottom: 4px solid var(--bg);
    background: var(--border-colour);
    height: var(--resizer-thickness);
    user-select: none;
  }

  .row-resizer::before {
    position: absolute;
    top: -8px;
    right: 0;
    bottom: -8px;
    left: 0;
    cursor: row-resize;
    content: '';
  }

  .row-handle {
    display: inline-flex;
    position: absolute;
    right: 50%;
    left: 50%;
    justify-content: center;
    align-items: center;
    transform: translate(-50%, -50%);
    z-index: 10;
    backdrop-filter: brightness(0%);
    cursor: ns-resize;
    box-shadow: var(--shadow);
    border: var(--border);
    border-radius: 999px;
    background: var(--surface-dark);
    padding: 0 6px;
    min-width: 34px;
    height: 16px;
    touch-action: none;
    pointer-events: auto;
  }

  .row-handle-dots {
    transform: translateY(-1px);
    opacity: 0.85;
    color: var(--text);
    font-weight: 700;
    font-size: 14px;
    line-height: 1;
  }

  .row-resizer:hover,
  .row-resizer.dragging {
    background: var(--accent);
  }

  .row-resizer:hover .row-handle,
  .row-resizer.dragging .row-handle {
    border-color: var(--accent);
    background: var(--surface-light);
  }

  .row-resizer:hover .row-handle-dots,
  .row-resizer.dragging .row-handle-dots {
    opacity: 1;
  }

  nav {
    display: flex;
    position: relative;
    justify-content: center;
    align-items: center;
    box-sizing: border-box;
    border-bottom: var(--border);
    width: 348px;
    height: 75px;
  }

  .brand-image {
    z-index: 2;
    height: 50px;
  }

  .sidebar {
    float: left;
    box-sizing: border-box;
    border-right: var(--border);
    width: 350px;
    height: 100%;
  }

  .events {
    display: flex;
    flex-direction: column;
    align-items: flex-start;
    box-sizing: border-box;
    padding: 20px 10px 20px 20px;
    padding-bottom: 0px;
    height: calc(100% - 75px);
    min-height: 0;
  }

  .livestream {
    position: relative;
    box-sizing: border-box;
    min-height: 160px;
  }

  .overlay-buttons {
    display: flex;
    position: absolute;
    top: 0;
    right: 0;
    flex-direction: column;
    align-items: flex-end;
    gap: 0.5rem;
    z-index: 25;
    padding: 1rem;
  }

  .overlay-buttons > div:first-of-type {
    display: flex;
    flex-direction: column;
    align-items: flex-end;
    gap: 0.5rem;
  }

  .overlay-buttons > .help-dropdown,
  .overlay-buttons > .user-info-dropdown {
    position: absolute;
    top: 1rem;
    right: 4rem;
    z-index: 20;
  }

  .overlay-icon-btn {
    display: inline-flex;
    justify-content: center;
    align-items: center;
    z-index: 25;
    backdrop-filter: blur(3px);
    transition:
      background 0.15s ease,
      border-color 0.15s ease,
      transform 0.05s ease;
    cursor: pointer;
    box-sizing: border-box;
    outline: none;
    border: var(--border);
    border-radius: 36px;
    background: var(--surface);
    padding: 0px 8px;
    min-width: 36px;
    height: 36px;
    color: var(--text);
  }

  .overlay-icon-btn svg {
    width: 16px;
    height: 16px;
  }

  .overlay-icon-btn p {
    transition:
      max-width 0.3s linear,
      padding-right 0.1s linear;
    box-sizing: border-box;
    max-width: 0;
    overflow: hidden;
    font-size: 0.8rem;
    white-space: nowrap;
  }

  .overlay-icon-btn:hover {
    background: var(--surface-light);
  }

  .overlay-icon-btn:hover:not(.open) p {
    padding-right: 4px;
    max-width: 150px;
  }

  .overlay-icon-btn.open {
    border-color: var(--accent);
    background: var(--surface-light);
  }

  .overlay-icon-btn.open p {
    transition:
      max-width 0s linear,
      padding-right 0s linear;
  }

  .overlay-icon-btn.danger {
    color: var(--error);
  }

  .title {
    color: var(--text);
    font-size: 1.75em;
  }

  .cameras {
    display: flex;
    position: relative;
    flex-direction: column;
    box-sizing: border-box;
    padding: 20px 20px 20px 15px;
    padding-bottom: 0px;
    min-height: 160px;
  }

  .cameras .title {
    padding-left: 5px;
  }

  .event {
    display: flex;
    position: relative;
    justify-content: space-between;
    align-items: center;

    z-index: 0;

    box-sizing: border-box;

    box-shadow: var(--shadow);
    border: var(--border);
    border-radius: var(--border-radius);
    background: var(--surface);
    padding: 15px 10px;
    width: 100%;
    min-height: 72px;
    color: var(--text);
    user-select: none;
  }

  .event:has(.info) {
    cursor: pointer;
  }

  .event:has(.info):hover {
    background: var(--surface-light);
  }

  .event .info {
    display: flex;
    flex-direction: column;
    justify-content: space-between;
    align-items: flex-end;
  }

  .event .info .spacer + p {
    font-size: 0.75rem;
  }

  .event .spacer {
    display: none;
    margin-right: 7px;
    margin-left: 7px;
    border-radius: 50%;
    background-color: var(--text-faint);
    width: 5px;
    height: 5px;
  }

  .event.center {
    justify-content: center;
  }

  .event.is-now {
    outline: 3px solid var(--accent-faint);
    outline-offset: -5px;
    border-color: var(--accent);
    color: var(--accent);
  }

  .event.is-soon:not(.is-now) {
    border-color: var(--accent-faint);
  }

  .event:last-child {
    margin-bottom: 0px;
  }

  .event-list {
    display: flex;
    flex: 1 1 auto;
    flex-direction: column;
    align-items: flex-start;
    gap: 10px; /* has flex-gap in modern Safari; fallback via .event + .event */
    box-sizing: border-box;
    margin-top: 10px;
    padding-right: 10px;
    width: 100%;
    min-height: 0;
    overflow-y: auto;
    -webkit-overflow-scrolling: touch;
    isolation: isolate;
  }

  .cameras {
    color: var(--text);
  }

  .device-list {
    display: flex;
    flex-direction: column;
    align-items: stretch;
    box-sizing: border-box;
    margin-top: 20px;
    padding-right: 1rem;
    padding-left: 12px;
    width: 100%;
    height: 100%;
    /* background-color: blue; */
    overflow-y: auto;
    scrollbar-gutter: stable;
  }

  .device-list .device {
    border-top: var(--border);
    padding-top: 0.5rem;
  }

  .device-list .device > p:first-child {
    font-size: 1.25rem;
  }

  .device-list .no-cameras {
    margin-bottom: 1rem;
    color: var(--text-faint);
  }

  .device-cameras {
    display: grid;
    grid-template-columns: repeat(var(--cols), 1fr);
    column-gap: 20px;
    row-gap: 20px;
    justify-items: left;
    margin-top: 10px;
    margin-bottom: 20px;
  }

  .camera {
    position: relative;
    width: 130px;
  }

  .icon {
    margin-bottom: 10px;
    border-radius: var(--border-radius);
    background-color: var(--surface-light);
    width: 128px;
    height: 128px;
  }

  .camera .cameraImage:hover,
  .camera .cameraImage.current-camera {
    outline: 3px solid var(--accent);
  }

  .camera .cameraImage.current-camera + * p {
    color: var(--accent);
  }

  .icon-group {
    display: flex;
    flex-grow: 1;
    align-items: center;
    margin-right: 1rem;
  }

  .icon-group .event-icon {
    margin-right: 8px;
  }

  .icon-group p:last-child {
    flex-grow: 1;
    text-align: right;
  }

  .icon-group p:last-child span {
    font-size: 0.75rem;
  }

  svg.event-icon {
    width: 20px;
    height: 32px;
  }

  .no-cam-wrapper {
    display: flex;
    flex-direction: column;
    justify-content: center;
    align-items: center;
    width: 100%;
    height: 100%;
    color: var(--text);
  }

  .no-cam-text {
    margin-top: 20px;
    font-size: 1.5em;
    text-align: center;
  }

  .device-list .loading-indicator {
    align-self: center;
    justify-self: center;
  }

  .date-buttons {
    display: flex;
    justify-content: space-between;
    align-items: center;
    box-sizing: border-box;
    margin-top: 20px;
    margin-bottom: 20px;
    width: 100%;
    height: 50px;
  }

  .date-buttons button {
    box-shadow: var(--shadow);
    border: var(--border);
    border-radius: var(--border-radius);
    background: var(--surface);
    padding: 10px 15px;
    color: var(--text);
  }

  .date-buttons button:not(:disabled) {
    cursor: pointer;
  }

  .offsetDec {
    margin-right: 10px;
  }

  .disable-right-buttons {
    opacity: 0.5;
  }

  .date-text {
    text-align: center;
  }

  .right-wrapper {
    display: flex;
  }

  @keyframes fadeIn {
    0% {
      opacity: 0;
    }

    100% {
      opacity: 1;
    }
  }

  .git-info {
    display: flex;
    flex-direction: column;
  }

  .version,
  .time {
    color: var(--text);
  }

  .logout-btn {
    mask-image: url('data:image/svg+xml,%3Csvg xmlns="http%3A%2F%2Fwww.w3.org%2F2000%2Fsvg" width="24" height="24" viewBox="0 0 24 24"%3E%3Cpath fill="red" d="m17 8l-1.4 1.4l1.6 1.6H9v2h8.2l-1.6 1.6L17 16l4-4l-4-4M5 5h7V3H5c-1.1 0-2 .9-2 2v14c0 1.1.9 2 2 2h7v-2H5V5Z"%2F%3E%3C%2Fsvg%3E');
    mask-position: center;
    mask-size: 85%;
    mask-repeat: no-repeat;
    background-color: var(--error);
    fill: var(--error);
    border: none;
    width: 48px;
    height: 48px;
  }

  .hidden {
    display: none;
  }

  .cameraImage {
    display: flex;
    position: relative;
    justify-content: center;
    align-items: center;
    cursor: pointer;
    margin-bottom: 10px;
    border-radius: var(--border-radius);
    background: var(--surface-dark);
    width: 130px;
    height: 130px;
    overflow: hidden;
  }

  .cameraImage img.preview:not(.fallback) {
    height: 100%;
  }

  .statusIndicator {
    position: absolute;
    top: -4px;
    left: -4px;
    border-radius: 8px;
    min-width: 4px;
    height: 8px;
    --color: var(--text-faint);
    z-index: 2;
    animation: 3s loadingStatusIndicator infinite;
    transition:
      background-color 0.5s ease,
      box-shadow 0.5s ease,
      opacity 0.5s ease;
    box-shadow: 0 0 0 0 transparent;
    background: var(--color);
    padding: 0 2px;
    color: var(--bg);
    font-size: 8px;
    line-height: 1;
    text-align: center;
  }

  @keyframes loadingStatusIndicator {
    0% {
      opacity: 1;
    }
    50% {
      opacity: 0.5;
    }
    100% {
      opacity: 1;
    }
  }

  .statusIndicator.offline {
    animation: none;
    --color: var(--error);
    box-shadow: 0 0 8px 2px var(--color);
  }

  .statusIndicator.online {
    animation: none;
    --color: var(--success);
    box-shadow: 0 0 8px 2px var(--color);
  }

  .statusIndicator.late {
    animation: none;
    --color: var(--warn);
    box-shadow: 0 0 8px 2px var(--color);
  }

  .statusIndicator.noData {
    animation: none;
  }

  .camera .marquee p {
    font-size: 1rem;
  }

  #movie-maker-modal {
    display: flex;
    position: fixed;
    flex-direction: column;
    justify-content: space-between;
    box-sizing: border-box;
    margin: auto;
    border: 0;
    border-radius: var(--border-radius);
    background-color: var(--surface);
    padding: 20px;
    width: 600px;
    height: 350px;
  }

  #movie-maker-modal:focus {
    outline: none;
  }

  #movie-maker-modal::backdrop {
    background-color: var(--modal-bg);
  }

  .modal-title {
    color: var(--text);
    font-size: 1.625em;
  }

  .modal-subtitle {
    margin-bottom: 10px;
    color: var(--text);
    font-size: 1.125em;
  }

  .modal-group {
    display: flex;
    justify-content: space-between;
    align-items: center;
  }

  .modal-group button {
    border: 0;
    border-radius: var(--border-radius);
    background-color: var(--surface-light);
    width: 80px;
    height: 50px;
    color: var(--text);
  }

  .modal-group input[type='datetime-local'] {
    box-sizing: border-box;
    border: var(--border);
    border-radius: var(--border-radius);
    background: var(--surface-light);
    padding: 20px;
    width: 350px;
    height: 50px;
    color: var(--text);
  }

  .modal-footer button {
    border: 0;

    border-radius: var(--border-radius);
    padding: 15px 25px;
  }

  .modal-wrapper:first-child {
    margin-bottom: 20px;
  }

  .modal-footer {
    display: flex;
    justify-content: space-between;
    align-items: center;
  }

  .modal-close {
    background: var(--error);
    color: var(--text);
  }

  .modal-accept {
    position: relative;
    background: var(--success);
    color: var(--text);
  }

  .movie-maker-wrapper {
    display: flex;
    position: fixed;
    justify-content: center;
    align-items: center;
    z-index: 999999;
    width: 100%;
    height: 100%;
  }

  .movie-maker-modal-backdrop {
    display: flex;
    justify-content: center;
    align-items: center;
    background-color: var(--modal-bg);
    width: 100%;
    height: 100%;
  }

  .movie-maker-wrapper.hidden {
    display: none;
  }

  .event-group {
    display: flex;
    justify-content: space-between;
    align-items: center;
    margin-bottom: 20px;
    width: 100%;
  }

  .clip-btn {
    cursor: pointer;
    margin-left: 10px;
    border: none;
    background-image: url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='24' height='24' viewBox='0 0 24 24'%3E%3Cpath fill='white' d='M14.75 7.46L12 3.93l1.97-.39l2.74 3.53l-1.96.39m6.87-1.36l-.78-3.92l-3.93.78l2.74 3.54l1.97-.4M4 20v-8h16v1.09c.72.12 1.39.37 2 .72V10H2v10a2 2 0 0 0 2 2h9.81c-.35-.61-.59-1.28-.72-2H4m7.81-11.95L9.07 4.5l-1.97.41l2.75 3.53l1.96-.39M4.16 5.5l-.98.19a1.995 1.995 0 0 0-1.57 2.35L2 10l4.9-.97L4.16 5.5M20 18v-3h-2v3h-3v2h3v3h2v-3h3v-2h-3Z'/%3E%3C/svg%3E%0A");
    background-position: center;
    background-size: 100%;
    background-repeat: no-repeat;
    background-color: transparent;
    width: 40px;
    height: 40px;
  }

  .modal-close:disabled {
    opacity: 0.8;
  }

  .hide-download-text {
    visibility: hidden;
  }

  .download-indicator {
    position: absolute;
    top: 0;
    right: 0;
    bottom: 0;
    left: 0;
  }

  .checkmark {
    margin-bottom: 20px;
    background-image: url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='120' height='120' viewBox='0 0 512 512'%3E%3Cpath fill='none' stroke='green' stroke-miterlimit='10' stroke-width='32' d='M448 256c0-106-86-192-192-192S64 150 64 256s86 192 192 192s192-86 192-192Z'/%3E%3Cpath fill='none' stroke='green' stroke-linecap='round' stroke-linejoin='round' stroke-width='32' d='M352 176L217.6 336L160 272'/%3E%3C/svg%3E");
    background-position: repeat;
    background-repeat: no-repeat;
    width: 120px;
    height: 120px;
  }

  .checkmark-box {
    display: flex;
    flex-direction: column;
    justify-content: center;
    align-items: center;
    color: var(--text);
  }

  .checkmark-box h2 {
    font-size: 1.625em;
  }

  .modal-body-checkmark {
    display: flex;
    justify-content: center;
    align-items: center;
    height: calc(100% - 1.625em);
  }

  /* Dropdown panel */
  .user-info-dropdown {
    z-index: 14;
    backdrop-filter: blur(3px);
    box-shadow: var(--shadow);
    border: var(--border);
    border-radius: var(--border-radius);
    background: var(--surface);
    padding: 10px 12px;
    min-width: 250px;
    max-width: 50vw;
    color: var(--text);
  }

  .user-info-dropdown .row {
    display: grid;
    grid-template-columns: 70px 1fr;
    align-items: center;
    gap: 8px;
    padding: 6px 2px;
    font-size: 0.95rem;
  }

  .user-info-dropdown .row + .row {
    border-top: var(--separator);
  }

  .user-info-dropdown .label {
    color: var(--text);
    font-weight: 600;
  }

  .user-info-dropdown .value {
    overflow: hidden;
    color: var(--text);
    text-overflow: ellipsis;
    white-space: nowrap;
  }

  .user-info-dropdown button {
    display: flex;
    justify-content: flex-start;
    gap: 1rem;
    cursor: pointer;
    margin-top: 6px;
    border: none;
    border-radius: var(--border-radius);
    background: var(--surface-dark);
    padding: 6px 10px;
    width: 100%;
    color: var(--text);
  }

  .user-info-dropdown button:hover {
    background: var(--surface-superlight);
  }

  .user-info-dropdown button img {
    width: 20px;
    height: 20px;
  }

  .user-info-dropdown .row.footer {
    display: flex;
    justify-content: space-between;
  }

  .user-info-dropdown .row.footer .value {
    color: var(--text-faint);
    font-size: 0.8rem;
  }

  /* Dropdown panel for shortcuts */
  .help-dropdown {
    z-index: 14;
    backdrop-filter: blur(3px);
    box-shadow: var(--shadow);
    border: var(--border);
    border-radius: var(--border-radius);
    background: var(--surface);
    padding: 14px 14px 10px;
    width: 500px;
    overflow: auto;
    color: var(--text);
  }

  .help-close {
    display: inline-flex;
    position: absolute;
    top: 8px;
    right: 8px;
    justify-content: center;
    align-items: center;
    z-index: 10;
    cursor: pointer;
    border: var(--border);
    border-radius: 50%;
    background: var(--surface-dark);
    width: 28px;
    height: 28px;
    color: var(--text);
  }

  /* Use a mask so the "X" inherits currentColor (same pattern as .clear-btn) */
  .help-close::before {
    mask: url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 24 24'%3E%3Cpath d='M18.3 5.7a1 1 0 0 0-1.4 0L12 10.6 7.1 5.7A1 1 0 0 0 5.7 7.1L10.6 12l-4.9 4.9a1 1 0 1 0 1.4 1.4L12 13.4l4.9 4.9a1 1 0 0 0 1.4-1.4L13.4 12l4.9-4.9a1 1 0 0 0 0-1.4z'/%3E%3C/svg%3E")
      no-repeat center / 100% 100%;
    background: currentColor;
    width: 16px;
    height: 16px;
    content: '';
  }

  .help-dropdown h4 {
    opacity: 0.9;
    margin: 8px 4px 6px;
    color: var(--text);
    font-size: 1rem;
  }

  .shortcut-grid {
    display: grid;
    grid-template-columns: 1fr;
    gap: 6px;
    margin: 0 0 6px;
    border-bottom: var(--separator);
    padding: 0 4px 8px;
    list-style: none;
  }

  .shortcut-grid li {
    display: flex;
    justify-content: space-between;
    align-items: center;
    gap: 8px;
  }

  .shortcut-grid .keys {
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
  }

  .shortcut-grid .desc {
    opacity: 0.9;
    text-align: right;
  }

  summary {
    cursor: pointer;
    user-select: none;
  }

  /* Pretty <kbd> badges */
  kbd {
    display: inline-block;
    margin-right: 4px;
    border: var(--border);
    border-radius: 6px;
    background: var(--surface-dark);
    padding: 2px 6px 0 6px;
    min-width: 1.6em;
    color: var(--text);
    font-size: 0.85rem;
    line-height: 1.4;
    font-family:
      ui-monospace, SFMono-Regular, Menlo, Consolas, 'Liberation Mono',
      monospace;
    user-select: none;
    text-align: center;
  }

  .hint {
    opacity: 0.9;
    margin: 6px 4px 0;
    color: var(--text);
    font-size: 0.85rem;
  }

  .device-filter {
    display: inline-flex;
    position: relative;
    align-items: center;
    min-width: 0;
  }

  .device-jump {
    flex: 0 1 auto;
    min-width: 0;
  }

  .device-search::-webkit-search-decoration,
  .device-search::-webkit-search-cancel-button {
    display: none;
  }

  .device-search::-ms-clear {
    display: none;
    width: 0;
    height: 0;
  }

  .device-search {
    outline: none;
    border: var(--border);
    border-radius: var(--border-radius);
    background: var(--surface);
    padding: 0 10px;
    min-width: 0;
    height: 36px;
    color: var(--text);
  }

  .device-search::placeholder {
    color: var(--text-faint);
  }

  .device-search:focus {
    border-color: var(--accent);
    background: var(--surface-light);
  }

  /* the custom clear button */
  .clear-btn {
    display: inline-flex;
    position: absolute;
    top: 50%;
    right: 8px;
    justify-content: center;
    align-items: center;
    transform: translateY(-50%);
    cursor: pointer;
    border: var(--border);
    border-radius: 50%;
    background: var(--surface-dark);
    padding: 0;
    width: 22px;
    height: 22px;
  }

  /* use a mask so the cross inherits currentColor */
  .clear-btn::before {
    display: block;
    mask-image: url('data:image/svg+xml,%3Csvg%20xmlns%3D%27http%3A%2F%2Fwww.w3.org%2F2000%2Fsvg%27%20viewBox%3D%270%200%2024%2024%27%3E%3Cpath%20d%3D%27M18.3%205.7a1%201%200%200%200-1.4%200L12%2010.6%207.1%205.7A1%201%200%200%200%205.7%207.1L10.6%2012l-4.9%204.9a1%201%200%201%200%201.4%201.4L12%2013.4l4.9%204.9a1%201%200%200%200%201.4-1.4L13.4%2012l4.9-4.9a1%201%200%200%200%200-1.4z%27%2F%3E%3C%2Fsvg%3E');
    mask-position: center;
    mask-size: 100% 100%;
    mask-repeat: no-repeat;
    background: currentColor;
    width: 12px;
    height: 12px;
    content: '';
  }

  .clear-btn {
    color: var(--text-faint);
  }

  .clear-btn:hover {
    background: var(--surface);
    color: var(--text);
  }

  .clear-btn:active {
    transform: translateY(-50%) scale(0.96);
  }

  .export-group {
    display: inline-flex;
    position: relative;
    align-items: stretch;
  }

  .modal-footer button {
    cursor: pointer;
  }

  .export-group .export-main {
    border-top-right-radius: 0;
    border-bottom-right-radius: 0;
  }

  .export-group .export-caret {
    border-left: var(--border);
    border-top-left-radius: 0;
    border-bottom-left-radius: 0;
  }

  .export-main {
    position: relative;
    min-width: 200px;
  }

  .export-caret {
    display: inline-flex;
    justify-content: center;
    align-items: center;
    padding: 0;
    width: 44px;
  }

  /* Dropdown menu */
  .export-menu {
    position: absolute;
    top: calc(100% + 6px);
    right: 0;
    z-index: 10;
    backdrop-filter: blur(3px);
    margin: 0;
    box-shadow: var(--shadow);
    border: var(--border);
    border-radius: var(--border-radius);
    background: var(--surface-light);
    padding: 6px;
    min-width: 230px;
    color: var(--text);
    list-style: none;
  }

  .export-menu li + li {
    border-top: var(--separator);
  }

  .export-menu .menu-item {
    cursor: pointer;
    border: 0;
    background: transparent;
    padding: 10px 8px;
    width: 100%;
    color: var(--text);
    text-align: left;
  }

  .export-menu .menu-item:disabled {
    opacity: 0.55;
    cursor: not-allowed;
    text-decoration: line-through;
  }

  .export-menu .menu-item:hover:not(:disabled) {
    background: var(--surface-superlight);
  }

  /* Header row for the Events list */
  .section-header {
    display: flex;
    flex-wrap: wrap;
    align-items: center;
    gap: 10px;
    width: 100%;
    min-width: 0;
  }

  .section-header .title {
    flex: 1 1 30vw;
    margin-right: auto;
    min-width: 0;
  }

  .events .section-header .title {
    flex: none;
  }

  .device-tools {
    display: flex;
    flex: 0 1 auto;
    flex-wrap: wrap;
    justify-content: stretch;
    gap: 10px;
    min-width: 0;
  }

  /* Filter buttons - visually consistent with .help-btn / .user-info-btn */
  .event-filters {
    display: inline-flex;
    gap: 8px;
  }

  .event-filter-btn {
    display: inline-flex;
    justify-content: center;
    align-items: center;
    transition:
      background 0.15s ease,
      border-color 0.15s ease,
      transform 0.05s ease,
      box-shadow 0.15s ease;
    cursor: pointer;
    outline: none;
    border: var(--border);
    border-radius: 50%;
    background: var(--surface);
    padding: 4px;
    width: 36px;
    height: 36px;
    color: var(--text);
  }

  .event-filter-btn:hover {
    background: var(--surface-light);
  }

  .event-filter-btn:active {
    transform: translateY(1px);
  }

  .event-filter-btn.active {
    box-shadow: inset 0 0 0 2px var(--accent-faint);
    border-color: var(--accent);
    background: var(--surface-light);
    color: var(--text);
    /* keep icon visible in active state */
  }

  .event-filter-btn .event-icon {
    max-width: 100%;
    max-height: 100%;
  }

  /* Panel layout (optional, just makes intent explicit) */
  .menu-panel {
    display: flex;
    flex-direction: column;
    align-items: flex-end; /* keeps it visually “under” the hamburger on the right */
    gap: 0.5rem;
  }

  /* Slide-down animation (Vue 3 transition classes) */
  .menu-drop-enter-from,
  .menu-drop-leave-to {
    transform: translateY(-8px) scale(0.98);
    opacity: 0;
  }

  .menu-drop-enter-active,
  .menu-drop-leave-active {
    transform-origin: top right; /* so it feels like it opens from the hamburger */
    transition:
      opacity 140ms ease,
      transform 140ms ease;
  }

  .menu-drop-enter-to,
  .menu-drop-leave-from {
    transform: translateY(0) scale(1);
    opacity: 1;
  }

  /* Optional: sweet little stagger for each button */
  .menu-drop-enter-active .menu-panel > * {
    transition:
      opacity 160ms ease,
      transform 160ms ease;
  }
  .menu-drop-enter-from .menu-panel > * {
    transform: translateY(-6px);
    opacity: 0;
  }
  .menu-drop-enter-to .menu-panel > * {
    transform: translateY(0);
    opacity: 1;
  }

  /* Stagger per child (tweak counts as needed) */
  .menu-drop-enter-active .menu-panel > *:nth-child(1) {
    transition-delay: 20ms;
  }
  .menu-drop-enter-active .menu-panel > *:nth-child(2) {
    transition-delay: 40ms;
  }
  .menu-drop-enter-active .menu-panel > *:nth-child(3) {
    transition-delay: 60ms;
  }
  .menu-drop-enter-active .menu-panel > *:nth-child(4) {
    transition-delay: 80ms;
  }

  /* Respect reduced-motion */
  @media (prefers-reduced-motion: reduce) {
    .menu-drop-enter-active,
    .menu-drop-leave-active,
    .menu-drop-enter-active .menu-panel > * {
      transition: none !important;
    }
  }

  .event + .event {
    margin-top: 10px;
  }

  .event .event-icon {
    flex-shrink: 0;
  }

  /* Flex overflow fixes for Safari */
  .right-pane,
  .cameras,
  .events,
  .event-list,
  .device-list {
    min-width: 0;
    min-height: 0;
  }

  /* Safari search input normalization */
  .device-filter .device-search {
    -webkit-appearance: none;
    appearance: none;
    line-height: 1.2;
    font: inherit;
  }
  .device-filter .clear-btn {
    flex: 0 0 auto;
  }

  /* ---- Safari-specific rendering fixes ---- */
  @supports (-webkit-touch-callout: none) or (not (translate: 0)) {
    /* Prevent flex children from overflowing and overlapping */
    .events,
    .event-list,
    .device-list,
    .sidebar {
      min-width: 0 !important;
      min-height: 0 !important;
    }
    .event {
      transform: none !important;
    }
    /* Make scrolling stable on iOS/macOS Safari */
    .event-list {
      -webkit-overflow-scrolling: touch;
    }
    /* Inputs inside flex rows shouldn't blow up */
    input[type='search'],
    input[type='text'],
    .searchbar input {
      width: 100%;
      min-width: 0 !important;
    }
  }

  video,
  canvas,
  .livestream,
  .livestream * {
    user-select: none;
  }

  .compat-modal-backdrop {
    display: flex;
    position: fixed;
    justify-content: center;
    align-items: center;
    z-index: 9999;
    backdrop-filter: blur(3px);
    inset: 0;
    background: var(--modal-bg);
  }
  .compat-modal {
    box-shadow: var(--shadow);
    border-radius: 1rem;
    background: var(--surface);
    padding: 20px;
    width: calc(100% - 2rem);
    max-width: 520px;
    color: var(--text);
  }
  .compat-modal h2 {
    font-size: 1.5rem;
  }
  .compat-modal p,
  .compat-modal h2 {
    margin-bottom: 1rem;
  }
  .compat-actions {
    display: flex;
    justify-content: flex-end;
    gap: 0.5rem;
  }
  .compat-actions .btn-primary,
  .compat-actions .btn-secondary {
    border: var(--border);
    border-radius: var(--border-radius);
    background: var(--surface-light);
    padding: 0.5rem 0.9rem;
    color: var(--text);
  }
  .compat-actions .btn-primary {
    background: var(--accent);
  }

  .homeview-video-wrapper {
    height: calc(100% - 40px);
  }

  .eventLoading {
    width: 100%;
    height: 20vh;
  }
</style>
